/**
 * MapView component.
 * Renders an leaflet/vue2leaflet map based on the mapViewData passed via props. Capture the map events, dealing with them or emitting events.
 * @uses storage defined in @see /src/store/modules/map-state
 *
 * Events that this component listens to:
 * @listens redrawAndFitMap [via eventBus] - event that will trigger a map redraw and refit bounds - expects {isMaximized: Boolean, guid: String}
 * @listens clearMap [via eventBus] - event that will trigger a map clear
 * @listens changeActiveRouteIndex [via eventBus] - event that will trigger active route index change
 * @listens placeFocusChanged [via eventBus] - updates the map center when a new place is selected *
 * Events emitted via eventBus:
 * @emits activeRouteIndexChanged
 * @emits setSideBarStatus
 * @emits mapLeftClicked
 * @emits mapRightClicked
 *
 * Local events emitted:
 * @emits zoomChanged
 * @emits markerDragged
 * @emits markerClicked
 * @emits onCreate
 * @emits mapCenterChanged
 * @emits directionsFromPoint
 * @emits directionsToPoint
 * @emits addRouteStop
 * @emits addDestinationToRoute
 * @emits setInputPlace
 */

import {
  LMap,
  LTileLayer,
  LLayerGroup,
  LTooltip,
  LPopup,
  LControlZoom,
  LControlAttribution,
  LControlScale,
  LWMSTileLayer,
  LControlLayers,
  LGeoJson,
  LPolygon,
  LCircle,
  LCircleMarker,
  LIcon,
  LMarker,
  LRectangle
} from "vue2-leaflet";

import routeData from "@/support/map-data-services/ors-response-data-extractors/route-data";
import ExtraInfoHighlight from "./components/extra-info-highlight/ExtraInfoHighlight";
import MapRightClick from "./components/map-right-click/MapRightClick";
import MapViewMarkers from "./components/map-view-markers/MapViewMarkers";
import LControlPolylineMeasure from "vue2-leaflet-polyline-measure";
import MapLeftClick from "./components/map-left-click/MapLeftClick";
import OrsLPolyline from "./components/ors-l-polyline/OrsLPolyline";
import AdminAreaLoader from "@/support/admin-area-loader";
import MyLocation from "./components/my-location/MyLocation";
import { GestureHandling } from "leaflet-gesture-handling";
import orsDictionary from "@/resources/ors-dictionary";
import LHeightGraph from "vue2-leaflet-height-graph";
import PolygonUtils from "@/support/polygon-utils";
import MapViewData from "@/models/map-view-data";
import drawLocales from "leaflet-draw-locales";
import mapDefinitions from "./map-definitions";
import constants from "@/resources/constants";
import I18nBuilder from "@/i18n/i18n-builder";
import appConfig from "@/config/app-config";
import GeoUtils from "@/support/geo-utils";
import Utils from "@/support/utils";
import theme from "@/config/theme";
import Place from "@/models/place";
import "vue2-leaflet-draw-toolbar";
import Leaflet from "leaflet";
import lodash from "lodash";
import { mapGetters } from "vuex";

// Import leaflet-related styles
import "leaflet-gesture-handling/dist/leaflet-gesture-handling.css";
import "leaflet-measure/dist/leaflet-measure.css";
import "vue-resize/dist/vue-resize.css";
import "leaflet/dist/leaflet.css";
import shadow from "../../assets/img/current_location_pointer_new.png";
import pin from "../../assets/img/place_black_36.svg";
import clock from "../../assets/img/clock.svg";
import placeSvg from "../../assets/img/place.svg";
import DengerousPoints from "@/assets/dengerousPoints.json";
import { getSunrise, getSunset } from 'sunrise-sunset-js'
import moment from 'moment'
import Toaster from "@/fragments/toaster/Toaster";

export default {
  components: {
    LMap,
    LTileLayer,
    OrsLPolyline,
    LLayerGroup,
    LTooltip,
    LPopup,
    LControlZoom,
    LControlAttribution,
    LControlScale,
    LControlLayers,
    "l-wms-tile-layer": LWMSTileLayer,
    LGeoJson,
    LPolygon,
    LCircle,
    LCircleMarker,
    LIcon,
    LMarker,
    LRectangle,
    LControlPolylineMeasure,
    ExtraInfoHighlight,
    MapRightClick,
    MapLeftClick,
    MyLocation,
    LHeightGraph,
    MapViewMarkers,
    appToaster: Toaster
  },
  props: {
    mapViewData: {
      required: true,
      type: MapViewData
    },
    height: {
      default: 300
    },
    initialZoom: {
      required: true,
      type: Number
    },
    showPopups: {
      type: Boolean,
      default: true
    },
    showControls: {
      type: Boolean,
      default: true
    },
    fitBounds: {
      type: Boolean,
      default: true
    },
    center: {
      type: Object,
      required: false
    },
    customTileProviderUrl: {
      type: String,
      required: false
    },
    shrunk: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      required: true
    },
    supportsDrawingTool: {
      type: Boolean,
      default: false
    },
    routingProfileIcon: {
      type: String,
      required: false
    },
    avoidPolygons: {
      type: Array,
      required: false
    }
  },
  data() {
    return {
      clock,
      placeSvg,
      pin,
      shadow: window.location.origin + "/" + shadow,
      tileProviders: [], // list of tiles provider that will be set via setProviders
      overlayerTileProviders: [], // list of overlay tiles provider that will be set via setProviders
      wmsOverlayerTileProviders: [], // list of WMS overlay tiles provider that will be set via setProviders
      layersPosition: "topright",
      map: null, // map object reference. it will will be defined later
      zoomLevel: null,
      mapDataBuilder: null,
      initialMaxZoom: appConfig.initialMapMaxZoom,
      localMapViewData: new MapViewData(), // we use a local copy of the mapViewData to be able to modify it
      mainRouteColor: theme.primary,
      alternativeRouteColor: constants.alternativeRouteColor,
      routeBackgroundColor: constants.routeBackgroundColor,
      guid: null,
      clickLatlng: null,
      myLocationActive: false,
      setDrawingTimeout: null,
      markerMoveTimeoutId: null,
      drawControlRef: null, // a reference to the drawing polygon component
      clickInsidePolygon: null,
      currentInnerHeight: null,
      showClickPopups: true,
      showActiveRouteData: true,
      showAlternativeRouteTooltip: true,
      dataBounds: null, // the current bounds of features displayed on the map
      myLocationMenuOpen: false,
      focusedPlace: null, // the place that must be focused among the ones visible on the map view (for search mode)
      highlightedRoutePointIndex: null, // a point on the route that must be highlighted (a Leaflet latLng)
      isAltitudeModalOpen: false,
      extraInfo: null, // Extra route info (waytypes, surface, steepness etc)
      featuresJustFitted: false,
      localAvoidPolygons: null,
      mapDataViewChangeDebounceTimeoutId: null,
      isDanger: false,
      iconSize: 64,
      staticAnchor: [16, 37],
      turn: 0,
      check_magnetometer: false,
      geojson: null,
      orientationCheck: false,
      areashow: null,
      a: [],
      setmarker: false,
      setmarkerLalLng: null,
      duration: "",
      distance: "",
      oneDuration: 0,
      oneDistance: 0,
      secondDuration: 0,
      secondDistance: 0,
      routeSafetyTime: false,
      routeSafety: "Balanced",
      currentlatlng: null,
      placelatlng: null,
      routehideshow: true,
      hostports: null,
      markeronedata: null,
      secendFeilds: null,
      markerremove: false,
      giraMarker: null,
      girastationone: null,
      girastationsecond: null,
      removeGiraStationone: null,
      removeGiraStationsecend: null,
      dangerponitfirst: null,
      dangerponitsecend: null,
      routeSafetyno: "3",
      removeHostportMarker: [],
      checkGiraStationStatus: false,
      isqueModal: false,
      addressGetWhenClickMap: null,
      queAns: null,
      hosportStatus: false,
      mobilehosportStatus: false,
      giraRoutePlaceOne: null,
      giraRoutePlaceSecond: null,
      RoutePlaceOne: null,
      RoutePlaceSecond: null,
      RoutePlaceOneRemove: null,
      RoutePlaceSecondRemove: null,
      upldateLocationEverySec: null,
      errorShow:false,
      insertHostportshowMobileView:true,

      hostportModalCloseOpenDropdwon : false,
      removeGiraMarker:[],
      TimeremoveClickEvent:null,
      TimeremoveClick:false,
      bounds:Leaflet.latLngBounds(Leaflet.latLng(-90, -200), Leaflet.latLng(90, 200)),
      baseurl: "https://routeplanner.cycleai.net/",
      touchstatus : true,
      navigationMode : false,
      cursorAddHostPort:false,
      polilineDraw :false,
      showLoading:false,
      addHostpot: null,
      countDengerousPoint:null,
      removePolygon: [],
      removeCircle:[],
      oneTimeAPICallHostpot: false,
      oneTimeAPICallAccident: false,
      removeAddHostpot:[],
      perceptionPollModal:false,
      imageOne:null,
      imageSecond:null,
      imageOneSrc:null,
      imageSecondSrc:null,
      location:Intl.DateTimeFormat().resolvedOptions().timeZone,
      rectangleColor: {
        1.0: "#FA1443",
        1.1: "#FB1A46",
        1.2: "#F81A43",
        1.3: "#F92043",
        1.4: "#F92442",
        1.5: "#F62940",
        1.6: "#F62B3F",
        1.7: "#FB3044",
        1.8: "#F83441",
        1.9: "#F8363E",
        2.0: "#F83A3C",
        2.1: "#F73E39",
        2.2: "#F84237",
        2.3: "#F84738",
        2.4: "#F64934",
        2.5: "#F74C32",
        2.6: "#F65230",
        2.7: "#F6542F",
        2.8: "#F5582D",
        2.9: "#F45D28",
        3.0: "#F66128",
        3.1: "#F66627",
        3.2: "#F56725",
        3.3: "#F66E22",
        3.4: "#F77424",
        3.5: "#F4761F",
        3.6: "#F67B1E",
        3.7: "#F6801C",
        3.8: "#F48418",
        3.9: "#F38718",
        4.0: "#F68E15",
        4.1: "#F49110",
        4.2: "#F79611",
        4.3: "#F59A0D",
        4.4: "#F49E0D",
        4.5: "#F5A009",
        4.6: "#F3A303",
        4.7: "#F5A603",
        4.8: "#F6A903",
        4.9: "#F4AA01",
        5.0: "#F1A900"
      }
      // rectangle: {
      //   bounds: [[47.341456, -1.397133], [47.303901, -1.243813]],
      //   style: { color: 'blue', weight: 3 }
      // }
    };
  },
  computed: {
    dynamicSize() {
      return [this.iconSize, this.iconSize * 1.15];
    },
    dynamicAnchor() {
      return [this.iconSize / 2, this.iconSize * 1.15];
    },
    showMyLocationControl() {
      return (
        this.supportsMyLocationBtn &&
        !this.isAltitudeModalOpen &&
        this.showControls
      );
    },
    /**
     * Determines if the my location btn is available
     * @returns {Boolean}
     */
    supportsMyLocationBtn() {
      let available =
        appConfig.supportsMyLocationBtn && !this.$store.getters.embed;
      return available;
    },
    /**
     * Determines if the distance measure tool is available
     * @returns {Boolean}
     */
    distanceMeasureToolAvailable() {
      let available = appConfig.distanceMeasureToolAvailable;
      return available;
    },
    /**
     * Determines if the accessibility tool is available
     * @returns {Boolean}
     */
    accessibilityToolAvailable() {
      let available = appConfig.accessibilityToolAvailable && this.showControls;
      return available;
    },
    /**
     * Determines if click on the map to pick a location is active
     * @returns {Boolean}
     */
    clickToPickActive() {
      return this.$store.getters.pickPlaceIndex !== null;
    },
    /**
     * Returns the non embedded mode URL.
     * When in embedded mode, a 'view on ors' btn is displayed and
     * the target url of this btn is the not embedded versions
     * @returns {String} url
     */
    nonEmbedUrl() {
      let url = location.href.split("/embed")[0];
      return url;
    },
    /**
     * Return the current applicable zoom level
     * based either on the zoomLevel attribute or
     * on the maxZoom
     * @returns {Number} zoom
     */
    zoom() {
      // const zoom = this.zoomLevel > 0 ? this.zoomLevel : this.maxZoom
      const zoom = this.zoomLevel > 0 ? this.zoomLevel : 13;
      let pixel = 20;
      if (zoom == 18) {
        pixel = 20;
      } else if (zoom == 17) {
        pixel = 10;
      } else if (zoom == 16) {
        pixel = 4;
      } else if (zoom == 15) {
        pixel = 2;
      } else if (zoom == 14) {
        pixel = 1;
      } else if (zoom == 13) {
        pixel = 1;
      } else {
        pixel = 0;
      }
      const collection = document.getElementsByClassName("circle-change");
      for (let i = 0; i < collection.length; i++) {
        collection[i].style.filter = "blur(" + pixel + "px)";
      }
      return zoom;
    },

    /**
     * Returns the map options
     * based on the embed mode value
     * and the show controls computed prop
     * @returns {Object}
     */
    mapOptions() {
      let options = {
        zoomControl: this.showControls,
        attributionControl: true,
        measureControl: true,
        gestureHandling: this.$store.getters.embed,
        gestureHandlingOptions: {
          text: this.$t("mapView.gestureHandling"),
          duration: 1000
        }
      };
      return options;
    },
    /**
     * Build and return the map center
     * based on the the current map center defined/set in the store
     * @returns {Latlng}
     */
    mapCenter() {
      let latlng = GeoUtils.buildLatLong(this.$store.getters.mapCenter);
      return latlng;
    },
    /**
     * Determines if only one marker if on the map view
     * @returns {Boolean}
     */
    hasOnlyOneMarker() {
      return this.markers && this.markers.length === 1;
    },
    /**
     * Determines if the brand over the
     * map view must be shown
     * @returns {Boolean}
     */
    showBrand() {
      return this.mapHeight > 450 || this.$store.getters.embed;
    },
    /**
     * Build and return the geojson options based on the
     * color defined as main
     * @returns {Object}
     */
    geojsonOptions() {
      return {
        style: { color: this.mainRouteColor, weight: "5" }
      };
    },
    /**
     * Return the geojson style options
     * using the value in constants object
     * @returns {Object}
     */
    geojsonOutlineOptions() {
      return { style: { color: constants.routeBackgroundColor, weight: "9" } };
    },
    /**
     * Build and return the active route data
     * based on the $store.getters.activeRouteIndex
     * @returns {Array} of latlng
     */
    activeRouteData() {
      if (this.localMapViewData.hasRoutes()) {
        // We must not change the original object
        const toBeTransformedMapViewData = this.localMapViewData.clone();

        // get the coordinates of the active route
        const route =
          toBeTransformedMapViewData.routes[
            this.$store.getters.activeRouteIndex
          ];
        // console.log(route)
        // route.geometry.coordinates = [[[38.726222, -9.148658, 69],[38.726271, -9.148704, 69]]]
        // Vue2-Leaflet, the component used to render data on the map, expect the coordinates in the [lat,lon] order,
        // but the GeoJSON format returned by ORS API contains coordinates in the [lon,lat] order.
        // So we invert them to provide what the component expects
        route.geometry.coordinates = GeoUtils.switchLatLonIndex(
          route.geometry.coordinates
        );
        return route;
      }
    },
    /**
     * Build and return an array of alternative routes
     * to be rendered on the map view
     * @returns {Array} alternativeRoutesData objects
     */
    alternativeRoutes() {
      const alternativeRoutesData = [];
      if (this.localMapViewData.hasRoutes()) {
        // We must not change the original object
        const toBeTransformedMapViewData = this.localMapViewData.clone();

        for (const key in toBeTransformedMapViewData.routes) {
          const index = Number(key);
          if (index !== this.$store.getters.activeRouteIndex) {
            // Vue2-Leaflet, the component used to render data on the map, expect the coordinates in the [lat,lon] order,
            // but the GeoJSON format returned by ORS API contains coordinates in the [lon,lat] order.
            // So we invert them to provide what the component expects
            toBeTransformedMapViewData.routes[
              key
            ].geometry.coordinates = GeoUtils.switchLatLonIndex(
              toBeTransformedMapViewData.routes[key].geometry.coordinates
            );
            toBeTransformedMapViewData.routes[key].properties.index = index;
            alternativeRoutesData.push(toBeTransformedMapViewData.routes[key]);
          }
        }
      }
      return alternativeRoutesData;
    },
    /**
     * Build and return the polygons to be rendered
     * on the map view based on the polygons
     * defined in the localMapViewData object.
     * As the renderer expect the polygons coords in the
     * [lat,lon] order, we switch the coords of each polygon
     * and also set the color and label based on the polygon
     * data and translations
     * @returns {Array} polygons
     */
    polygons() {
      const polygons = [];
      if (this.localMapViewData) {
        const translations = this.$t("global.units");
        translations.polygon = this.$t("global.polygon");
        // We must not change the original object
        const mapViewDataToBeTransformed = this.localMapViewData.clone();

        for (const key in mapViewDataToBeTransformed.polygons) {
          const polygon = mapViewDataToBeTransformed.polygons[key];

          // Vue2-Leaflet, the component used to render data on the map, expect the coordinates in the [lat,lon] order,
          // but the GeoJSON format returned by ORS API contains coordinates in the [lon,lat] order.
          // So we invert them to provide what the component expects
          let flattenCoords = PolygonUtils.flatCoordinates(
            polygon.geometry.coordinates
          );
          polygon.latlngs = GeoUtils.switchLatLonIndex(flattenCoords);
          polygons.push(polygon);
        }
      }
      return polygons;
    },
    /**
     * Build and return an array of marker objects
     * based on the on the places defined on the localMapViewData
     * @returns {Array} of markers
     */
    markers() {
      let markersMapViewData = this.localMapViewData.clone();
      if (markersMapViewData.places.length > 0) {
        let isRoute =
          markersMapViewData.hasRoutes() ||
          this.mode === constants.modes.directions;
        let markers = GeoUtils.buildMarkers(
          markersMapViewData.places,
          isRoute,
          this.focusedPlace
        );
        if (markersMapViewData.places.length === 1) {
          // console.log(markers);
          // this.markerremove = true;
          // if (this.secendFeilds !== null) {
          //   this.map.removeLayer(this.secendFeilds);
          // }
          // console.log(markers);
          // const latlng = GeoUtils.buildLatLong(
          //   markers[0].position.lat,
          //   markers[0].position.lng
          // );
          // console.log(latlng);
          // setTimeout(() => {
          //   this.setMapCenter(latlng);
          // }, 800);
          // this.$store.commit("currentLocation", markersMapViewData.places);
          // markers = Leaflet.marker(
          //   [markers[0].position.lat, markers[0].position.lng],
          //   {
          //     icon: markers[0].icon
          //   }
          // );
          // markers.addTo(this.map);
          // this.secendFeilds = markers;
          // console.log(this.secendFeilds);
          // markers = this.$root.appHooks.run("markersCreated", markers);
        } else {
          this.markerremove = false;
          markers = this.$root.appHooks.run("markersCreated", markers);

          return markers;
        }
      }
    },
    /**
     * Build and return an array of marker objects
     * based on the POIs places
     * @returns {Array} of markers
     */
    pois() {
      if (this.localMapViewData.pois.length > 0) {
        let poisMarkers = GeoUtils.buildMarkers(this.localMapViewData.pois);
        poisMarkers = this.$root.appHooks.run(
          "poisMarkersCreated",
          poisMarkers
        );
        return poisMarkers;
      } else {
        return [];
      }
    },
    /**
     * Determines the max zoom applicable
     * for the map based on the map view data nax zoom or on the
     * initial max zoom
     * @returns {Number}
     */
    maxZoom() {
      if (this.localMapViewData.maxZoom) {
        return this.localMapViewData.maxZoom;
      }
      return this.initialMaxZoom;
    },
    /**
     * Build and return the circle marker
     * based on the clickLatlng position
     * to show to the user where s/he has clicked
     * @returns {Object}
     */
    circleMarker() {
      if (this.clickLatlng) {
        return {
          center: GeoUtils.buildLatLong(
            this.clickLatlng.lat,
            this.clickLatlng.lng
          ),
          radius: 8
        };
      }
    },
    /**
     * Build and return the my position marker object
     * based on the current location stored in the store
     * @returns {Object}
     */
    myPositionMarker() {
      if (this.$store.getters.currentLocation) {
        const markerData = {
          center: GeoUtils.buildLatLong(
            this.$store.getters.currentLocation.lat,
            this.$store.getters.currentLocation.lng
          ),
          radius: 9,
          accuracy: this.$store.getters.currentLocation.accuracy,
          opacity: 1
        };
        return markerData;
      }
    },
    /**
     * Determines if a route stop can be added
     */
    canAddStop() {
      const can =
        !Array.isArray(this.markers) ||
        this.markers.length < appConfig.maxPlaceInputs;
      return can;
    },
    /**
     * Return the current map polyline measures options
     * @returns {Object} options
     */
    polylineMeasureOptions() {
      const options = mapDefinitions.polylineMeasureOptions(
        this.$t("mapView.polylineMeasure")
      );
      this.$root.appHooks.run("polylineMeasureOptionsBuilt", options);
      return options;
    },

    /**
     * Return the current map drawing options
     * @returns {Object} options
     */
    drawOptions() {
      const options = mapDefinitions.drawOptions(
        this.$t("mapView.youCantIntersectPolygons")
      );
      this.$root.appHooks.run("drawingOptionsBuilt", options);
      return options;
    },

    /**
     * Returns the current map height prop
     * @returns {Number} height
     */
    mapHeight() {
      return this.height;
    },

    /**
     * Determines if the directions mode is active
     */
    isInDirectionsMode() {
      return constants.modes.directions === this.mode;
    },

    /**
     * If polyline is draggable
     * @returns {Boolean}
     */
    isPolylineDraggable() {
      return this.isInDirectionsMode && !this.$store.getters.embed;
    },
    /**
     * Determines if the fit features button is visible
     * @returns boolean
     */
    canFitFeatures() {
      let available = appConfig.fitAllFeaturesToolAvailable;
      if (
        available &&
        (this.localMapViewData.hasPlaces() ||
          this.localMapViewData.hasRoutes() ||
          this.localAvoidPolygons.length > 0)
      ) {
        return true;
      }
      return false;
    },
    /**
     * If an active route data must be shown
     * @returns {Boolean} show
     */
    displayActiveRouteData() {
      const show = this.activeRouteData && this.showActiveRouteData;

      if (this.activeRouteData !== undefined && this.routehideshow) {
        const humanizedData = GeoUtils.getHumanizedTimeAndDistance(
          this.activeRouteData.summary,
          this.$t("global.units")
        );
        if (humanizedData) {
          this.eventBus.$emit('removeActiveLocation')
          this.routeSafetyTime = true;
          let distance = ((this.oneDistance  + this.secondDistance) / 1000).toFixed(1)
          if (!this.checkGiraStationStatus) {
            this.duration = humanizedData.duration;
            this.distance = humanizedData.distance;
          }
          else {
            if(this.checkGiraStationStatus)
            {
              if(this.$store.getters.dangeruousPoints !== '5')
              {
                this.distance = `${(Number(distance) + Number(this.activeRouteData.summary.distance)).toFixed(2)} Km`;
                const durationObj = moment.duration((Number(this.activeRouteData.summary.duration) + Number(this.oneDuration) + Number(this.secondDuration)), 'seconds') // the duration value is expected to be always in seconds
                let durationSegments = {
                  days:
                    durationObj._data.days > 0
                      ? durationObj._data.days + "days"
                      : '',
                  hours:
                    durationObj._data.hours > 0
                      ? durationObj._data.hours + ' ' + "hours"
                      : '',
                  minutes:
                    durationObj._data.minutes > 0
                      ? durationObj._data.minutes + ' ' + "min"
                      : '',
                  // seconds:
                  //   durationObj._data.seconds > 0
                  //     ? durationObj._data.seconds + ' ' + "seconds"
                  //     : ''
                }
                this.duration = `${durationSegments.days} ${durationSegments.hours} ${durationSegments.minutes}`
              }
            }
          }
          this.map.removeLayer(this.setmarkerLalLng);
          this.map.removeLayer(this.secendFeilds);

          // let markers = GeoUtils.buildMarkersRoute([this.RoutePlaceOne], 0);
          // let polygongira = Leaflet.marker(
          //   [markers[0].position.lat, markers[0].position.lng],
          //   { icon: markers[0].icon }
          // );
          // polygongira.addTo(this.map);
          setTimeout(() => {
            this.eventBus.$emit("dynamicHight");
            this.eventBus.$emit("dynamicWidth");
          }, 500);
          if (this.currentlatlng !== null && this.placelatlng !== null) {
            const placeDistanceInKM = this.getDistanceFromLatLonInKm(
              this.currentlatlng.lat,
              this.currentlatlng.lng,
              this.placelatlng.lat,
              this.placelatlng.lng
            ).toFixed(0);
            if (placeDistanceInKM < 50) {
              this.navigationstartmethods();
            } else {
              this.eventBus.$emit("navigationStop");
            }
          }
          this.eventBus.$emit("setdirectioncenter");
        }
      }
      return show;
    },

    /**
     * Determines if the current active
     * route is draggable based on app mode
     * @returns {Boolean} isDraggable
     */
    activeRouteIsDraggable() {
      let isDraggable = this.mode === constants.modes.directions;
      return isDraggable;
    },
    /**
     * Return the accessibility btn top position
     * based on the current map view height
     * @returns {String} height
     */
    accessibilityBtnTopPosition() {
      const height = `${this.height - 60}px`;
      return height;
    },
    /**
     * Returns the options object for the height graph component
     * @returns {Object}
     */
    lHeightGraphOptions() {
      let mappings = undefined;
      let activeRoute = this.localMapViewData.routes[
        this.$store.getters.activeRouteIndex
      ];
      let heightGraphTranslations = this.$t("mapView.heightGraph");

      // Build the mapping for the extra info
      // that will be displayed in the graph
      // including the translation and the color
      // associated to each extra value
      let extras = lodash.get(activeRoute, "properties.extras");
      if (extras) {
        let dict = orsDictionary;
        let translations = this.$t("orsDictionary");
        mappings = {};
        // Set the mapping for each extra info key
        for (let extraKey in extras) {
          mappings[extraKey] = {};
          let extra = extras[extraKey];
          let extraDict = dict[extraKey];

          // Build the map for each extra summary value
          for (let summaryKey in extra.summary) {
            let summary = extra.summary[summaryKey];
            let map = {
              text:
                translations[extraDict[summary.value]] ||
                extraDict[summary.value],
              color: dict.colors[extraKey][summaryKey]
            };
            mappings[extraKey][summary.value] = map;
          }
        }
      }
      let options = {
        parser: "ors",
        mappings: mappings,
        translation: heightGraphTranslations
      };
      return options;
    },
    /**
     * Build the map view data for leaflet height graph
     * As leaflet height graph does not support alternative routes
     * we have to put the active feature (according the active route index)
     * in the first index (0) where the component wil looks for
     * @returns {Object} rawData
     */
    localMapViewDataRawData() {
      let mapViewData = this.localMapViewData.clone();
      mapViewData.rawData.features[0] =
        mapViewData.rawData.features[this.$store.getters.activeRouteIndex];
      return mapViewData.rawData;
    },
  },
  watch: {
    /**
     * Every time the response data changes
     * the map builder is reset and the
     * map data is reloaded
     */
    mapViewData: {
      handler: function() {
        this.refreshMapViewData();
      },
      deep: true
    },
    avoidPolygons: {
      handler: function() {
        // When the avoidPolygons prop changes, we copy its value to a
        // local instance so that we can modify it when necessary
        this.localAvoidPolygons = Utils.clone(this.avoidPolygons);
      //   let Old = localStorage.getItem('setOldDangerousPoint');
      //   let New = localStorage.getItem('getDangerousPoint');
      //   console.log(this.localAvoidPolygons)
      //   console.log(this.removePolygon)
      //   console.log(this.removeCircle)
      // if(Old == "3" && New == "4") {
        
      // }
      // else if(Old == "4" && New == "3") {
      //   let ele = DengerousPoints.data.filter(element => {
      //     return element.marker_color <= '4' && element.marker_color >= '3'
      //   })
      //   this.removePolygon && this.removePolygon.map(data => {
      //     // console.log(data._latlngs[0][0].lat)
      //     ele && ele.map(value => {
      //       if(Number(data._latlngs[0][0].lat).toFixed(6) ===Number(value.lat).toFixed(6)) {
      //         this.map.removeLayer(data)
      //       }
      //     })
      //   })
      // }
      // else if(Old == "3" && New == "5") {
      //   console.log('h3')
      // }
      // else if(Old == "5" && New == "3") {
      //   console.log('h4')
      // }
      // else if(Old == "4" && New == "5") {
      //   console.log('h5')
      // }
      // else if(Old == "5" && New == "4") {
      //   console.log('h6')
      // }
        this.loadAvoidPolygons();
      },
      deep: true
    },
    'isqueModal' : function() {
      if(!this.isqueModal) {
        this.cursorAddHostPort = false
        this.removeInsertHostportLinkClickable()
        this.eventBus.$emit('tooltopClose')
      }
    },
    'perceptionPollModal' : function () {
      if(!this.perceptionPollModal) {
        this.hidesidebar()
      }
    },
    /**
     * When the prop supportsDrawingTool
     * changes run the setDrawingTool
     * if the prop value is true
     * @param {Boolean} newVal
     */
    supportsDrawingTool(newVal) {
      if (newVal) {
        this.setDrawingTool();
      }
    },
    /**
     * Once the map view is shrunk
     * we have to run the setDrawingTool
     * utility again
     */
    shrunk() {
      this.setDrawingTool();
    },
    /**
     * When the prop showPopups changes
     * copy its value to the local
     * attribute
     * @param {*} newVal
     */
    showPopups(newVal) {
      this.showClickPopups = newVal;
    },
    /**
     * When the prop height changes
     * run the adjust map method
     * to make sure that it fits the
     * current view
     */
    height() {
      this.adjustMap();
    },
    /**
     * When the prop mode changes
     * set the isAltitudeModalOpen attribute
     * based on the current mode
     */
    mode() {
      // Altitude modal must be hidden if mode is not directions or roundTrip
      if (
        this.mode !== constants.modes.directions &&
        this.mode !== constants.modes.roundTrip
      ) {
        this.isAltitudeModalOpen = false;
      }
    },
    /**
     * When the center prop value changes
     * run the centerChanged method that will
     * store the current location on localStorage
     */
    center: {
      handler: function() {
        this.centerChanged();
      },
      deep: true
    },
  },
  methods: {
    // render perception image get
    imageGetAPI () {
      let leftImg = Math.floor((Math.random() * 5000))
      let leftAngle = Math.floor((Math.random() * 4)) * 90
      this.imageOne = leftImg + '_' + leftAngle + '.jpg'
      this.imageOneSrc = 'https://cycleai.blob.core.windows.net/perception-pool/Images/'+leftImg+'_'+ leftAngle+'.jpg'
      let rightImg = Math.floor((Math.random() * 5000))
      let rightAngle = Math.floor((Math.random() * 4)) * 90
      this.imageSecond = rightImg + '_' + rightAngle + '.jpg'
      this.imageSecondSrc = 'https://cycleai.blob.core.windows.net/perception-pool/Images/'+rightImg+'_'+ rightAngle+'.jpg'
    },
    // render perceptionPoll vote store in database
    insertPerceptionPollVote (data) {
      let id = this.retrieveUser();
      fetch(this.baseurl + "/api/insertPerceptionPollVote?user_id=" + id + '&image_name1=' + this.imageOne + '&image_name2=' +  this.imageSecond + '&vote=' + data + '&location=' + this.location, {
        method: "GET",
        headers: {
          "Content-type": "application/json;charset=UTF-8"
        }
      })
      .then(response => response.json())
      .then(data => {
        this.imageGetAPI();
      })
    },
    // store unique id
    retrieveUser() {
      var user = localStorage.getItem("uuid");
      if (user == null) {
          user = this.uuidv4()
          localStorage.setItem("uuid", user);
      }
      return user;
    },
    //  generate unique every time
    uuidv4() {
      return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
          (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
      );
    }, 
    /**
     * Refresh map view data after the prop mapViewData has changed
     * We use a debounce in order to apply only the last change
     */
    //sidebar close
    hidesidebar(event) { 
      if(!this.navigationMode) {
        if(this.cursorAddHostPort) {
          this.mapLeftClick(event);
        }
        if (this.$store.getters.leftSideBarOpen) {
        } else {
          if (
            document.getElementsByClassName("leaflet-popup-content-wrapper")
              .length > 0
          ) {
          } else {
            // if(this.$lowResolution) {
              // console.log(event)
              // this.mapLeftClick(event);
            // }
            
          }
        }
        if (this.$lowResolution) {
          this.$store.commit("setLeftSideBarIsOpen", false);
          this.eventBus.$emit("hidedropdown");
          this.eventBus.$emit("inputPlaceshow");
          this.eventBus.$emit("buttonstoggleshowonly");
          this.eventBus.$emit("settingsButton");
          this.eventBus.$emit("settingsviewshowhide");
          // document.getElementsByClassName("text-fields1")[0].style.marginBottom = "unset";
          document
            .getElementsByClassName("text-fields1")[0]
            .setAttribute("style", "marginBottom:unset;");
          // document.getElementsByClassName("text-fields2")[0].style.marginBottom = "unset";
          document
            .getElementsByClassName("text-fields2")[0]
            .setAttribute("style", "marginBottom:unset;");
          setTimeout(() => {
            this.eventBus.$emit("dynamicHight");
          }, 300);
          if(this.cursorAddHostPort) {
            this.mapLeftClick(event);
            // setTimeout(() => {
            //   document.getElementsByClassName('v-dialog__content--active')[1].setAttribute("style","z-index:190;")
            // }, 150);
          }
          return;
        }
        if (!this.$lowResolution) {
          document.body.classList.add("modal-open");
        }

        this.$store.commit("setLeftSideBarIsOpen", false);
        this.eventBus.$emit("toasterOff");
        // this.eventBus.$emit('buttonstoggleshow')
      }
      
    },
    // navigation start mode
    navigationstartmethods() {
      this.eventBus.$emit("navigationStart");
    },
    // get distance two lanlng
    getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
      var R = 6371; // Radius of the earth in km
      var dLat = this.deg2rad(lat2 - lat1); // deg2rad below
      var dLon = this.deg2rad(lon2 - lon1);
      var a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(lat1)) *
          Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      var d = R * c; // Distance in km
      return d * 1000;
    },
    // convert deg to radius
    deg2rad(deg) {
      return deg * (Math.PI / 180);
    },
    refreshMapViewData() {
      // When the mapViewData prop changes, we copy its value to a
      // local instance so that we can modify it when necessary
      const context = this;
      if (this.mapDataViewChangeDebounceTimeoutId) {
        clearTimeout(this.mapDataViewChangeDebounceTimeoutId);
      }
      this.mapDataViewChangeDebounceTimeoutId = setTimeout(function() {
        // Create a new instance of MapViewData and set all the props into the local instance
        context.localMapViewData = context.mapViewData.clone();

        let changes = Utils.getObjectsDiff(
          context.localMapViewData,
          context.mapViewData
        );
        let different = changes.different;
        // Only refresh local data if the change was not only the opacity
        if (
          different.length !== 1 ||
          (different.length === 1 && different[0].indexOf(".opacity") > 0)
        ) {
          context.loadMapData();
          context.refreshAltitudeModal();
        }
      }, 500);
    },
    /**
     * Refresh the altitude modal (force a destroy and a rebuild)
     * with the new data
     */
    refreshAltitudeModal() {
      let previousAltitudeModalState = this.isAltitudeModalOpen;
      this.isAltitudeModalOpen = false;
      const context = this;
      setTimeout(() => {
        context.isAltitudeModalOpen = previousAltitudeModalState;
      }, 100);
    },
    /**
     * Move the map center according the direction
     * @param {String} direction
     */
    moveMapCenter(direction) {
      var offset;
      switch (direction) {
        case "left":
          offset = this.map.getSize().x * 0.15;
          this.map.panBy(new Leaflet.Point(-offset, 0), { animate: true });
          break;
        case "right":
          offset = this.map.getSize().x * 0.15;
          this.map.panBy(new Leaflet.Point(offset, 0), { animate: true });
          break;
        case "up":
          offset = this.map.getSize().y * 0.15;
          this.map.panBy(new Leaflet.Point(0, -offset), { animate: true });
          break;
        case "down":
          offset = this.map.getSize().y * 0.15;
          this.map.panBy(new Leaflet.Point(0, offset), { animate: true });
          break;
      }
    },
    /**
     * Update the view center
     */
    centerChanged() {
      if (this.center) {
        this.setMapCenter(this.center);
        // If current location is defined, update it
        if (
          this.$store.getters.currentLocation ||
          this.mode === constants.modes.search
        ) {
          const currentLocation = {
            lat: this.center.lat,
            lng: this.center.lng,
            accuracy: 50
          };
          this.$store.commit("currentLocation", currentLocation);
        }
      }
    },
    /**
     * Handles the marker clicked event by
     * preventing the propagation and emitting
     * a local event
     * @param {*} place
     * @param {*} event
     * @emits markerClicked
     */
    markerClicked(place) {
      this.$emit("markerClicked", place);
    },

    /**
     * Handle the isochrone polygon clicked event
     * @param {Number} index
     * @param {Object} polygon
     * @param {Event} event
     */
    isochroneClicked(index, polygon) {
      let isochronePopupContainerRef = this.$refs[
        `isochronePopupContainer${index}`
      ];
      isochronePopupContainerRef = Array.isArray(isochronePopupContainerRef)
        ? isochronePopupContainerRef[0]
        : isochronePopupContainerRef;
      this.$root.appHooks.run("beforeOpenIsochronePopup", {
        isochronePopupContainerRef,
        polygon
      });
    },
    /**
     * Deals with the map center changed event triggered by the vue2leaflet
     * component. Get the current map center, set it via setMapCenter and
     * define the current myLocationActive based on the last map center
     * @param {*} event
     * @emits mapCenterMoved
     */
    mapMoved(event) {
      clearTimeout(this.TimeremoveClickEvent)
      const center = event.target.getCenter();

      const distance = GeoUtils.calculateDistanceBetweenLocations(
        this.$store.getters.mapCenter,
        center,
        "m"
      );
      // We only consider that the center changed if it
      // changes more than 50 meters from the previous center
      if (distance > 50) {
        this.setMapCenter(center);
        this.storeMapBoundsAndSetMapAsReady();
        const data = {
          center: this.$store.getters.mapCenter,
          distance: distance
        };
        this.$emit("mapCenterMoved", data);
      }
      // If the map is moved by the user the myLocation is reset it in order to define if
      // my location circle should continue to appear as map center
      if (this.myLocationActive) {
        const distance = GeoUtils.calculateDistanceBetweenLocations(
          this.$store.getters.currentLocation,
          center,
          "m"
        );
        // For some unknown reason, when a new map center
        // is defined it may varies from the location acquired
        // via browser location api. So, we are considering that
        // if this distance is less then 50 meters, then the my
        // location must still be considered as active
        this.myLocationActive = distance < 50;
      }
    },
    /**
     * Handle the alternative route index selected event
     * @param {*} index
     * @param {*} event
     * @emits activeRouteIndexChanged
     */
    alternativeRouteIndexSelected(index, event) {
      event.originalEvent.stopPropagation();
      event.originalEvent.preventDefault();
      this.eventBus.$emit("activeRouteIndexChanged", index);
      this.setActiveRouteIndex(index);
    },
    /**
     * Change the current active route index
     * @param {*} index
     */
    setActiveRouteIndex(index) {
      const context = this;
      this.$store.commit("activeRouteIndex", index);

      // We just want to disable the showClickPopups
      // temporarily, so we get the original state
      // set it as false and after a two seconds
      // we restore the original value
      const showPopupBefore = this.showClickPopups;
      this.showClickPopups = false;
      setTimeout(() => {
        context.showClickPopups = showPopupBefore;
      }, 2000);

      // Force the active route polyline to render again
      // after a timeout in order to make sure it
      // is over the other polylines
      const showActiveRouteDataBefore = this.showActiveRouteData;
      this.showActiveRouteData = false;
      setTimeout(() => {
        context.showActiveRouteData = showActiveRouteDataBefore;
      }, 100);
    },

    /**
     * When a layer is selected in the layer control
     * remove the expand class so that it is closed
     * after the selection
     */
    baseLayerChanged() {
      const layerControl = document.querySelector(".leaflet-control-layers");
      if (layerControl) {
        layerControl.classList.remove("leaflet-control-layers-expanded");
      }
    },
    /**
     * We watch the box model for changes and update the internal closed data that is used to control the visibility of the box
     * @param {Object} event
     * @emits zoomChanged
     */
    zoomed(event) {
      this.zoomLevel = event.sourceTarget._zoom;
      if (!this.featuresJustFitted && !this.hasOnlyOneMarker) {
        this.storeMapBoundsAndSetMapAsReady();
        this.$emit("zoomChanged", {
          zoom: event.sourceTarget._zoom,
          map: this.map,
          context: this
        });
      } else {
        // If the zoom was changed programmatically
        // reset the flag to false, as it has already
        // been accomplished its goal for one zoom event cycle
        this.featuresJustFitted = false;
      }
      
    },
    
    /**
     * Set the map tile providers available
     */
    setProviders() {
      this.tileProviders = mapDefinitions.getProviders();
      setTimeout(() => {
        this.overlayerTileProviders = mapDefinitions.getOverlayerProviders();
        this.wmsOverlayerTileProviders = mapDefinitions.getWmsOverlayerTileProviders();

        let hookData = {
          tileProviders: this.tileProviders,
          overlayerTileProviders: this.overlayerTileProviders,
          wmsOverlayerTileProviders: this.wmsOverlayerTileProviders,
          context: this
        };
        this.$root.appHooks.run("layerProvidersLoaded", hookData);
      }, 1000);
    },
    /**
     * Update markers label
     */
    updateMarkersLabel() {
      // Before returning the markers update the label of the marker using the places data in the localMapViewData storage
      if (this.localMapViewData.hasPlaces()) {
        for (const key in this.localMapViewData.places) {
          const humanizedIndex = Number(key) + 1;
          this.localMapViewData.places[key].index = humanizedIndex;
        }
      }
    },
    /**
     * Handles the marker move
     * by creating a debounce in order to
     * recalculate the route and update the map
     * view. A 1 second delay is applied to avoid
     * subsequent marker move updates and get the new
     * position only when the movement has ended
     * @param {*} event
     */
    markerMoved(event) {
      // Only marker changes that are a result of user interaction are treated here.
      // With vue2-leaflet version 2.5.2 the event.originalEvent is not  an instance of
      // window.PointerEvent anymore and use parent window.MouseEvent instead
      if (
        event.originalEvent instanceof window.MouseEvent ||
        event.originalEvent instanceof window.TouchEvent
      ) {
        clearTimeout(this.markerMoveTimeoutId);
        this.markerMoveTimeoutId = setTimeout(() => {
          this.markerDragEnd(event);
        }, 1000);
      }
    },
    /**
     * Add stop via polyline drag
     * @param {*} data
     * @emits addRouteStop
     */
    addStopViaPolylineDrag(data) {
      let closestPlaceIndex;
      data.latlng = data.event.target.getLatLng();
      // Get the closest index based on the use stop/route optimization setting
      if (this.$store.getters.mapSettings.useStopOptimization) {
        closestPlaceIndex = GeoUtils.getClosestPlaceIndex(
          data.latlng,
          this.localMapViewData.places
        );
      } else {
        const activeRouteDataPolyline = this.activeRouteData.geometry
          .coordinates;
        closestPlaceIndex = GeoUtils.getStopIndexFromSourcePoint(
          this.localMapViewData.places,
          activeRouteDataPolyline,
          data.draggedFromIndex
        );
      }
      data.injectIndex = closestPlaceIndex;
      this.$emit("addRouteStop", data);
    },
    /**
     * Remove a marker/place when in directions or isochrones mode
     * @param {*} event
     * @param {*} markerIndex
     * @emits removePlace
     */
    removePlace(markerIndex) {
      if (this.markers[markerIndex]) {
        let place = this.markers[markerIndex].place;
        let data = { place, index: markerIndex };
        this.$emit("removePlace", data);
      }
    },
    /**
     * Remove a marker/place when in directions or isochrones mode
     * @param {*} event
     * @param {*} markerIndex
     * @emits directChanged
     */
    markAsDirectFromHere(markerIndex) {
      if (this.markers[markerIndex]) {
        this.markers[markerIndex].place.direct = !this.markers[markerIndex]
          .place.direct;
        let place = this.markers[markerIndex].place;
        let data = { place, index: markerIndex };
        this.$emit("directChanged", data);
      }
    },
    /**
     * Deal with the marker drag end event
     * getting the marker index and emitting and event
     * @param {*} event
     * @emits markerDragged
     */
    markerDragEnd(event) {
      const targetLatlng = event.oldLatLng;
      let markerIndex = null;

      // Find the marker index by comparing the lat and long
      for (let index = 0; index < this.markers.length; index++) {
        const markerP = this.markers[index].position;
        if (
          markerP.lat === targetLatlng.lat &&
          markerP.lng === targetLatlng.lng
        ) {
          // update the marker position
          this.markers[index].position.lat = event.latlng.lat;
          this.markers[index].position.lng = event.latlng.lng;

          // set the marker index
          markerIndex = index;
          break;
        }
      }
      // If found, emits the markerDragged event
      if (markerIndex !== null) {
        const marker = this.markers[markerIndex];
        marker.inputIndex = markerIndex;
        this.$emit("markerDragged", marker);
      }
    },

    /**
     * Set the map center
     * @param {*} latlng
     * @use localStorage
     * @emits mapCenterChanged
     */
    setMapCenter(latlng) {
      if (latlng) {
        let mapSettings = this.$store.getters.mapSettings;
        const previousCenter = Utils.clone(mapSettings.mapCenter);

        if (previousCenter.toString() !== latlng.toString()) {
          this.$emit("mapCenterChanged", latlng);
        }
      } else {
        // TODO: stop using appRouteData, receive places as a prop?
        const routePlaces = this.$store.getters.appRouteData.places;
        if (routePlaces.length === 0 || routePlaces[0].isEmpty()) {
          if (this.center) {
            this.setMapCenter(this.center);
          } else {
            this.setPreviousMapCenter();
          }
        } else {
          this.setPreviousMapCenter();
        }
      }
      // let sensor = new Magnetometer();
      // sensor.start();
      //   sensor.onreading = () => {
      //    this.check_magnetometer=true
      //   }
    },

    /**
     * Set the previous center as map center
     */
    setPreviousMapCenter() {
      let mapSettings = this.$store.getters.mapSettings;
      if (mapSettings.mapCenter) {
        this.setMapCenter(mapSettings.mapCenter);
      }
    },

    /**
     * Store map bounds and set map as ready
     * @emits mapReady
     * @stores mapBounds
     */
    storeMapBoundsAndSetMapAsReady() {
      const buildBoundaries = bounds => {
        const boundary = {
          rect: {
            min_lon: bounds._southWest.lng,
            max_lon: bounds._northEast.lng,
            min_lat: bounds._southWest.lat,
            max_lat: bounds._northEast.lat
          }
        };

        return boundary;
      };
      this.getMapObject().then(map => {
        const bounds = map.getBounds();
        const mapBounds = buildBoundaries(bounds);
        let newBounds = JSON.stringify(bounds);
        let oldBounds = JSON.stringify(this.$store.getters.mapBounds);
        if (newBounds !== oldBounds) {
          this.$store.commit("mapBounds", mapBounds);
          this.$root.appHooks.run("mapBoundsChanged", {
            mapBounds,
            map: map,
            context: this
          });
        }
        this.$emit("mapReady", map);
      });
    },

    /**
     * To load map data we use the map builder service
     * and then once it is ready, we set the local data from
     * the data returned by the builder
     * and then set the bounds, fit the bounds and redraw the map
     * If the response data does not contains a geojson
     * then the promise resolver will return an object with the expected
     * props but all of then containing null values. This will not cause a fail
     *
     */
    loadMapData() {
      if (this.localMapViewData.hasPlaces()) {
        this.defineActiveRouteIndex();
        this.updateMarkersLabel();
        if (this.hasOnlyOneMarker) {
          this.setFocusedPlace(this.localMapViewData.places[0]);
        }
        if (
          this.mode === constants.modes.place &&
          this.hasOnlyOneMarker &&
          appConfig.showAdminAreaPolygon
        ) {
          this.loadAdminArea();
        } else {
          this.fitFeaturesBounds();
        }
      }
    },

    /**
     * Set the map view zom level when focused on an specific place
     * @param {Place} place
     */
    setFocusedPlace(place) {
      let layer = place.layer || place.properties.layer;
      if (layer) {
        this.zoomLevel = GeoUtils.zoomLevelByLayer(layer);
        this.setMapCenter(place.getLatLng());
      }
    },
    /**
     * Load admin area for region, city and county
     */
    loadAdminArea() {
      let place = this.markers[0].place;
      let adminAreaLoader = new AdminAreaLoader();
      let context = this;
      adminAreaLoader
        .getAdminAreaPolygon(place)
        .then(polygons => {
          context.localMapViewData.polygons = context.localMapViewData.polygons.concat(
            polygons
          );
          context.fitFeaturesBounds(true);
        })
        .catch(err => {
          console.log(err);
        });
    },
    /**
     * Build and set bounds based on localMapViewData
     */
    buildAndSetBounds() {
      let polylineData = [];

      if (this.extraInfo) {
        polylineData = this.buildExtraInfoBoundsPolyline();
        this.dataBounds = GeoUtils.getBounds([], polylineData);
      } else {
        // Add the routes coordinates to the polyline that must
        // be considered to the all features bounds
        for (const rKey in this.localMapViewData.routes) {
          if (this.localMapViewData.routes[rKey].geometry.coordinates) {
            const coords = this.localMapViewData.routes[rKey].geometry
              .coordinates;
            polylineData = polylineData.concat(coords);
          }
        }
        // Add the polygons coordinates to the polyline that must
        // be considered to  the all features bounds
        for (const pKey in this.localMapViewData.polygons) {
          const polygon = this.localMapViewData.polygons[pKey];
          if (polygon) {
            var coords = PolygonUtils.flatCoordinates(
              polygon.geometry.coordinates
            );
            polylineData = polylineData.concat(coords);
          }
        }
        // Build the all features bounds taking into consideration
        // the places and the roues/polygons polyline
        if (this.localMapViewData.hasPlaces() || polylineData.length > 0) {
          let places = Place.getFilledPlaces(this.localMapViewData.places);
          this.dataBounds = GeoUtils.getBounds(places, polylineData);
        } else {
          this.dataBounds = null;
        }
      }
    },
    /**
     * Build the polyline that contains the extra info to be used in the bounds definition
     * @returns {Array} polylineData
     */
    buildExtraInfoBoundsPolyline() {
      let polylineData = [];
      const coordinates = this.localMapViewData.routes[
        this.$store.getters.activeRouteIndex
      ].geometry.coordinates;
      const highlightData = routeData.buildHighlightedPolylines(
        coordinates,
        this.extraInfo
      );
      for (let key in highlightData) {
        let polylines = highlightData[key].polylines;
        for (let plKey in polylines) {
          polylineData = polylineData.concat(polylines[plKey]);
        }
      }
      return polylineData;
    },
    /**
     * Make sure the active route index is valid
     */
    defineActiveRouteIndex() {
      if (this.localMapViewData.hasRoutes()) {
        const maxRouteIndexValid = this.localMapViewData.routes.length - 1;
        if (this.$store.getters.activeRouteIndex > maxRouteIndexValid) {
          this.setActiveRouteIndex(0);
        }
      }
    },
    /**
     * Redraw the map component by faking a window resize event
     * This is a hack to force leaflet redraw/resize correctly the maps
     * in the case when there are two map viewers and the container of one of them
     * is resized.
     * The candidates map.invalidateSize() and map.eachLayer(function(layer){layer.redraw();});
     * have not worked at all on this case
     * @see https://github.com/Leaflet/Leaflet/issues/694
     * @dispatch resize event
     */
    redrawMap() {
      return new Promise(resolve => {
        setTimeout(() => {
          window.dispatchEvent(new Event("resize"));
          resolve();
        }, 10);
      });
    },

    /**
     * Emit the right click event according the parameter received
     * This methods emits only one of the events listed below at a time
     * @param {Object} data { eventName:..., clickLatlng: ...}
     * @emits directionsFromPoint
     * @emits directionsToPoint
     * @emits addRouteStop
     * @emits addDestinationToRoute
     */
    handleRightClickEvent(data) {
      if (data.eventName === "centerHere") {
        this.setMapCenter(data.clickLatlng);
      } else {
        this.prepareDataAndEmitRightClickEvent(data);
      }
    },

    /**
     * Update localMapViewData places
     * @param {Object} data {clickLatlng: latlng, eventName:String}
     * @emits directionsFromPoint
     * @emits directionsToPoint
     * @emits addRouteStop
     * @emits addDestinationToRoute
     */
    prepareDataAndEmitRightClickEvent(data) {
      let place = new Place(data.clickLatlng.lng, data.clickLatlng.lat);
      place.resolve().then(() => {
        const dataPassed = { latlng: data.clickLatlng, place };
        this.$emit(data.eventName, dataPassed);
      });
    },

    /**
     * Emit the event that will trigger the Set the
     * directions to place contained in the data object
     * @param {*} data
     * @emits directionsToPoint
     */
    directionsToPoint(data) {
      this.$emit("directionsToPoint", data);
    },
    /**
     * Emit the event that will trigger the setInputPlace to place contained in the data object
     * @param {Number} placeIndex
     * @param {Place} place
     * @emits setInputPlace
     */
    setInputPlace(placeIndex, placeInputId, place) {
      let data = {
        pickPlaceIndex: placeIndex,
        placeInputId: placeInputId,
        place: place
      };
      this.$emit("setInputPlace", data);
    },

    /**
     * Determines if the given bounds Array is valid
     * @param {*} dataBounds
     * @returns Boolean
     */
    isValidBounds(dataBounds) {
      if (!dataBounds || !Array.isArray(dataBounds) || dataBounds.length < 2) {
        return false;
      }
      return (
        dataBounds[0].lat !== null &&
        dataBounds[0].lon !== null &&
        dataBounds[1].lat !== null &&
        dataBounds[1].lon !== null
      );
    },
    /**
     * Fit all the features by adjusting the zoom level
     * @param force - force the fit ignoring fit options
     */
    fitFeaturesBounds(force = false) {
      if (this.fitBounds === true || force) {
        const context = this;

        // get and use the fit bounds option to determine if the fit should be run
        const maxFitBoundsZoom = this.hasOnlyOneMarker
          ? context.zoomLevel
          : this.maxZoom;

        return new Promise(resolve => {
          context.buildAndSetBounds();
          // If the map object is already defined
          // then we can directly access it
          if (context.map && context.isValidBounds(context.dataBounds)) {
            context.fit(maxFitBoundsZoom);
          } else {
            // If not, it wil be available only in the next tick
            context.$nextTick(() => {
              context.buildAndSetBounds();
              if (
                context.$refs.map &&
                context.isValidBounds(context.dataBounds)
              ) {
                context.map = context.$refs.map.mapObject; // Work as expected when wrapped in a $nextTick
                context.fit(force, maxFitBoundsZoom);
              }
              resolve();
            });
          }
        });
      }
    },

    /**
     * Handles the fit features hit btn and
     * emits a setSidebarStatus event that will adjust
     * the map to show all loaded features
     * @emits setSidebarStatus
     */
    fitAllFeatures() {
      this.fitFeaturesBounds(true);

      // If the app is in a low resolution mode
      // hide the sidebar so that the features can be seen
      if (this.$lowResolution) {
        // this.eventBus.$emit('setSidebarStatus', true)
      }
    },

    /**
     * Fit the bounds of the map considering the data bounds defined
     * @param {Boolean} force
     * @param {Number} maxFitBoundsZoom integer
     */
    fit(maxFitBoundsZoom) {
      if (this.dataBounds) {
        // We set the max zoom in level and then fit the bounds
        // Temporally disabled the zoom Level setting to check impacts (it seems not to be necessary anymore)

        // To make it work properly we have to wait a bit
        // before fitting the map bounds
        const context = this;
        setTimeout(() => {
          // The fit may affect the zoom level.
          // So we set the programmatically zoom flag to let
          // other methods to have this information if
          // they need, specially method `zoomed`
          context.featuresJustFitted = true;
          context.map.fitBounds(context.dataBounds, {
            padding: [20, 20],
            maxZoom: maxFitBoundsZoom
          });
          context.storeMapBoundsAndSetMapAsReady();

          // Yeah, it is not nice to have nested timeout
          // but we need it to make sure that this flag is only
          // set as false after a while so other components
          // have time to treat this before it is set to false again
          setTimeout(() => {
            context.featuresJustFitted = false;
          }, 500);
        }, 400);
      }
    },
    /**
     * Handle the polygon click event and show the polygon data in a pop up
     * @param {*} polygon
     */
    polygonInfoClick(polygon) {
      this.infoDialog(polygon.label, null, {
        code: polygon.data,
        resizable: true,
        zIndex: 1001
      });
    },

    /**
     * Handle the map right click event,
     * Set the clickLatlng current value
     * and emits events that will trigger the displaying
     * of the right click floating-context menu
     * @param {Object} event
     * @emits mapRightClicked (via eventBus)
     */
    mapRightClick(event) {
      if (this.showClickPopups) {
        const insidePolygon = this.isPointInsidePolygons(event.latlng);
        if (!insidePolygon) {
          const mapEl = this.$refs.map.$el;
          const data = { event, mapEl, canAddStop: this.canAddStop };
          // Event to be catch by the MapRightClick.vue component
          this.eventBus.$emit("mapRightClicked", data);
        }
        this.clickLatlng = { lat: event.latlng.lat, lng: event.latlng.lng };
      } else if (this.$store.getters.isSidebarVisible) {
        this.eventBus.$emit("setSidebarStatus", false);
      }
    },

    /**
     * Handle the map left click event
     * Set the clickLatlng current value
     * and emits events that will trigger the displaying
     * of the place info pop up box
     * @param {Object} event
     * @emits setSidebarStatus (via eventBus)
     * @emits mapLeftClicked (via eventBus)
     */
    mapLeftClick(event) {
      if(this.insertHostportshowMobileView && !this.hostportModalCloseOpenDropdwon) {
        this.isqueModal = true;
      }
    //   var latlng = new google.maps.LatLng(event.latlng.lat, event.latlng.lng);
    //   var geocoder = new google.maps.Geocoder();
    //   geocoder.geocode({ 'latLng': latlng },  (results, status) =>{
    //     if (status !== google.maps.GeocoderStatus.OK) {
    //       console.log(status);
    //     }
    //     // This is checking to see if the Geoeode Status is OK before proceeding
    //     if (status == google.maps.GeocoderStatus.OK) {
    //         console.log(results[0].geometry.location.lat(),results[0].geometry.location.lng());
    //         var address = (results[0].formatted_address);
    //     }
    // });
      if (this.$store.getters.pickPlaceIndex !== null) {
        this.pickPlaceViaClick(event);
      } else {
        let polygonInEditMode = this.isThereAnPolygonInEditMode();
        if (polygonInEditMode) {
          this.getMapObject().then(map => {
            this.saveAvoidPolygonChanges(polygonInEditMode, map);
          });
        } else {
          // If in low resolution and sidebar is open, then left click on the map
          // must close the side bar to allow the user to interact with the map.
          // If not then the normal left click handler must be executed
          if (this.$store.getters.isSidebarVisible && this.$lowResolution) {
            this.eventBus.$emit("setSidebarStatus", false);
          }
          this.handleShowLeftClickPlaceInfo(event);
        }
      }
    },

    /**
     * Check if a polygon is in edit mode
     * and if yes, return it
     * @returns {Object|false}
     */
    isThereAnPolygonInEditMode() {
      let polygons = this.extractAvoidPolygonsFromMap();
      for (let key in polygons) {
        let polygon = polygons[key];
        if (polygon.editing && polygon.editing._enabled) {
          return polygon;
        }
      }
      return false;
    },

    /**
     * Handle the left click when the place info box must be shown
     * @param {*} event
     * @emits mapLeftClicked
     */
    handleShowLeftClickPlaceInfo(event) {
      const drawPolygonToolbarActive = this.lodash.get(
        this.drawControlRef,
        "_toolbars.draw._activeMode"
      );
      const clickedOverPolyline =
        event.originalEvent && event.originalEvent.clickedOverPolyline === true;
      if (
        this.showClickPopups &&
        !drawPolygonToolbarActive &&
        !clickedOverPolyline
      ) {
        const insidePolygon = this.isPointInsidePolygons(event.latlng);
        const data = { event, insidePolygon };
        this.eventBus.$emit("mapLeftClicked", data);
        this.clickLatlng = { lat: event.latlng.lat, lng: event.latlng.lng };
      }
    },

    /**
     * Pick place via click when and cal setInputPlace that will emit 'setInputPlace'
     * @param {*} event
     * @uses pickPlaceIndex store getter
     */
    pickPlaceViaClick(event) {
      let place = new Place(event.latlng.lng, event.latlng.lat);
      let context = this;
      let pickPlaceIndex = context.$store.getters.pickPlaceIndex;
      let pickPlaceId = context.$store.getters.pickPlaceId;
      place.resolve().then(() => {
        context.setInputPlace(pickPlaceIndex, pickPlaceId, place);
        // Once a place was picked up,
        // remove the store pick place data
        context.$store.commit("pickPlaceIndex", null);
        context.$store.commit("pickPlaceId", null);
      });
    },

    /**
     * Check if the a lat lng point is inside in one of the map polygons
     * If it is returned the polygon points array
     * @param {Latlng} latlng
     * @param {*} lng
     * @returns {Boolean|Array}
     */
    isPointInsidePolygons(latlng) {
      for (const key in this.localAvoidPolygons) {
        let polygon = this.localAvoidPolygons[key];
        const coords = GeoUtils.switchLatLonIndex(
          polygon.geometry.coordinates[0]
        );
        const inside = PolygonUtils.isInsidePolygon(latlng, coords);
        if (inside) {
          return coords;
        }
      }
      return false;
    },
    /**
     * Adjust the map dimensions and redraw it according the current window size
     * @dispatch resize event
     */
    adjustMap() {
      window.dispatchEvent(new Event("resize"));

      const context = this;

      // After map container box is resized
      // we need to wait a little bit
      // to redraw the map and then
      // wait a little bit more to fit the bounds
      setTimeout(() => {
        // Redraw the map and then wait
        context.redrawMap().then(() => {
          setTimeout(() => {
            // After redrawing and waiting
            // fit the bounds
            context.fitFeaturesBounds();
          }, 500);
        });
      }, 500);
    },

    /**
     * Update the current browser's/user's location on the map
     * If the location can not be determined, show a toaster with this error
     * @param {Boolean} showLocation
     * @uses currentLocation
     */
    updateMyLocationUpdateEverySec(showLocation = false) {
      this.$gtag.event("my_location_button_click", {
        event_category: "location",
        event_label: "User clicked on the my location button",
        value: "my_location_button_click"
      });
      const context = this;
      if (showLocation) {
        GeoUtils.getBrowserLocation()
          .then(location => {
            context.$store.commit("currentLocation", location);
            context.myLocationActive = showLocation;
            // Set returned location as map center
            const placeName = "Your location";
            const place = new Place(location.lng, location.lat, placeName, {
              properties: { layer: "venue" }
            });
            this.eventBus.$emit("currentlatlngobj", place);
            const latlng = GeoUtils.buildLatLong(location.lat, location.lng);

            // setTimeout(() => {
            //   this.setMapCenter(latlng);
            //   setTimeout(() => {
            //     this.zoomLevel = 18;
            //   }, 800);
            // }, 800);
          })
          .catch(error => {
            // const message = this.getPositionErrorMessage(error)
            // context.showWarning(message, { timeout: 0 })
            // context.myLocationActive = false
            // console.log(error)
          });
      } else {
        // context.myLocationActive = false
      }
    },
    updateMyLocationButtonClick(showLocation = false) {
      this.$gtag.event("my_location_button_click", {
        event_category: "location",
        event_label: "User clicked on the my location button",
        value: "my_location_button_click"
      });
      const context = this;
      if (showLocation) {
        GeoUtils.getBrowserLocation()
          .then(location => {
            context.$store.commit("currentLocation", location);
            context.myLocationActive = showLocation;
            // Set returned location as map center
            const placeName = "Your location";
            const place = new Place(location.lng, location.lat, placeName, {
              properties: { layer: "venue" }
            });
            this.eventBus.$emit("currentlatlngobj", place);
            const latlng = GeoUtils.buildLatLong(location.lat, location.lng);
            clearInterval(this.upldateLocationEverySec);
            setTimeout(() => {
              // this.setMapCenter(latlng);
              this.upldateLocationEverySec = setInterval(() => {
                // this.updateMyLocationUpdateEverySec(true);
              }, 1000);
              setTimeout(() => {
                // this.zoomLevel = 18;
              }, 800);
            }, 800);
          })
          .catch(error => {
            // const message = this.getPositionErrorMessage(error)
            // context.showWarning(message, { timeout: 0 })
            // context.myLocationActive = false
            // console.log(error)
          });
      } else {
        // context.myLocationActive = false
      }
    },
    updateMyLocation(showLocation = false) {
      const context = this;
      if (showLocation) {
        GeoUtils.getBrowserLocation()
          .then(location => {
            context.$store.commit("currentLocation", location);
            context.myLocationActive = showLocation;

            // Set returned location as map center
            const latlng = GeoUtils.buildLatLong(location.lat, location.lng);
            this.setMapCenter(latlng);
          })
          .catch(error => {
            // const message = this.getPositionErrorMessage(error)
            // context.showWarning(message, { timeout: 0 })
            // context.myLocationActive = false
            // console.log(error)
          });
      } else {
        // context.myLocationActive = false
      }
    },
    updateMyLocationFirstTime(showLocation = false) {
      const context = this;
      if (showLocation) {
        GeoUtils.getBrowserLocation()
          .then(location => {
            context.$store.commit("currentLocation", location);
            context.myLocationActive = showLocation;

            // Set returned location as map center
            const latlng = GeoUtils.buildLatLong(location.lat, location.lng);
            this.setMapCenter(latlng);
            setTimeout(() => {
              // this.zoomLevel = 18;
              this.map.flyTo([location.lat, location.lng], 18, {
                animate: true,
                duration: 1 // in seconds
              });
            }, 500);
          })
          .catch(error => {
            const message = this.getPositionErrorMessage(error);
            context.showWarning(message, { timeout: 0 });
            // context.myLocationActive = false
            // console.log(error)
          });
      } else {
        // context.myLocationActive = false
      }
    },

    /**
     * Get position error message according error code
     * @param {*} error
     * @returns {String} message
     * @see https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError
     */
    getPositionErrorMessage(error) {
      // Set the default message
      let message = this.$t("mapView.acquirePositionErrors.generic");
      if (typeof error === "object") {
        switch (error.code) {
          case 1:
            message = this.$t("mapView.acquirePositionErrors.permissionDenied"); // PERMISSION_DENIED
            break;
          case 2:
            message = this.$t("mapView.acquirePositionErrors.unavailable"); // POSITION_UNAVAILABLE
            break;
          case 3:
            message = this.$t("mapView.acquirePositionErrors.timeout"); // TIMEOUT
            break;
        }
      }
      return message;
    },
    /**
     * Get map Object
     * @returns {Promise}
     */
    getMapObject() {
      let context = this;
      return new Promise(resolve => {
        if (context.map) {
          resolve(context.map);
        } else {
          context.$nextTick(() => {
            context.map = context.$refs.map.mapObject;
            resolve(context.map);
          });
        }
      });
    },
    /**
     * Set the drawing tool. Avoid multiple calls by debouncing
     */
    setDrawingTool() {
      if (!this.$store.getters.embed && appConfig.supportsAvoidPolygonDrawing) {
        if (this.setDrawingTimeout) {
          clearTimeout(this.setDrawingTimeout);
        }

        // If the map object is already defined, go ahead
        if (this.$refs.map && this.$refs.map.mapObject) {
          this.setAvoidPolygonDrawingTool();
        } else {
          // if not, wait a second and execute it again
          this.setDrawingTimeout = setTimeout(() => {
            this.setDrawingTool();
          }, 1000);
        }
      }
    },
    /**
     * Set drawing polygon tool to the map object
     * @see https://github.com/DenisCarriere/Leaflet.draw.locales to check the locales supported
     * @listens map.draw:created
     */
    setAvoidPolygonDrawingTool() {
      // Get a reference to the map object
      const map = this.$refs.map.mapObject;

      if (!this.showControls && this.drawControlRef) {
        map.removeControl(this.drawControlRef);
        this.drawControlRef = null;
      } else {
        // If the drawing controller is already attached
        // we have nothing to do here
        if (this.drawControlRef) {
          return;
        }

        // Tell the leaflet drawing locale to use the current app locale
        let shortLocale = I18nBuilder.getShortLocale(this.$i18n.locale);
        let locale;
        try {
          locale = drawLocales(shortLocale);
        } catch (error) {
          shortLocale = I18nBuilder.getShortLocale(this.$i18n.fallbackLocale);
          locale = drawLocales(shortLocale);
        }

        // Override the tooltip message
        let avoidPolygonBtnTranslations = {
          avoidPolygon: this.$t("mapView.defineAvoidPolygon"),
          avoidRectangle: this.$t("mapView.defineAvoidRectangle")
        };
        // Run the hook that mey modify the translations
        this.$root.appHooks.run(
          "avoidPolygonBtnTranslations",
          avoidPolygonBtnTranslations
        );

        // Set the translations to the buttons
        locale.draw.toolbar.buttons.polygon =
          avoidPolygonBtnTranslations.avoidPolygon;
        locale.draw.toolbar.buttons.rectangle =
          avoidPolygonBtnTranslations.avoidRectangle;

        // Set th custom draw locale to the leaflet draw locale
        Leaflet.drawLocal = locale;

        // Add the draw control with custom locale to the map
        this.drawControlRef = new Leaflet.Control.Draw(this.drawOptions);
        map.addControl(this.drawControlRef);

        const context = this;

        // Add listener to draw created,
        map.on("draw:created", function(e) {
          context.avoidPolygonCreated(e, map);
        });
      }
    },

    /**
     * Set polygon properties
     * @param {Object} polygon
     * @param {Object} polygonData
     */
    setAvoidPolygonProperties(polygon, polygonData = null) {
      // define polygon feature prop.
      // It will be returned when we get the geojson
      // representation of the polygon
      polygon.feature = polygon.feature || {};
      polygon.feature.type = polygon.feature.type || "Feature";
      polygon.feature.properties = polygon.feature.properties || {};
      polygon.feature.properties.avoidPolygon = true;

      // Append the properties from polygon data
      if (polygonData && polygonData.properties) {
        for (let key in polygonData.properties) {
          polygon.feature.properties[key] = polygonData.properties[key];
        }
      }
    },

    /**
     * Handle polygon created
     * @param {Object} event
     * @param {Object} map
     */
    avoidPolygonCreated(event, map) {
      var polygon = event.layer;
      this.setAvoidPolygonProperties(polygon);
      polygon.feature.properties.type = event.layerType;
      let context = this;
      polygon.addTo(map);
      polygon.on("click", function() {
        context.onAvoidPolygonClicked(polygon, map);
      });
      let expectedPromise = this.$root.appHooks.run("avoidPolygonCreated", {
        polygon,
        map,
        context
      });

      // If a promise is returned
      if (expectedPromise instanceof Promise) {
        expectedPromise
          .then(() => {
            context.notifyAvoidPolygonsChanged();
          })
          .catch(err => {
            console.log(err);
          });
      } else {
        context.notifyAvoidPolygonsChanged();
        context.showSuccess(context.$t("mapView.avoidPolygonSaved"));
      }
    },
    /**
     * Save avoid polygon changes
     * @param {Object} polygon
     * @param {Object} map
     */
    saveAvoidPolygonChanges(polygon, map) {
      let context = this;
      let expectedPromise = this.$root.appHooks.run("avoidPolygonEdited", {
        polygon,
        map,
        context
      });

      // If a promise is returned
      if (expectedPromise instanceof Promise) {
        expectedPromise
          .then(() => {
            polygon.editing.disable();
            polygon.closePopup();
            context.notifyAvoidPolygonsChanged();
          })
          .catch(err => {
            console.log(err);
          });
      } else {
        polygon.editing.disable();
        polygon.closePopup();
        context.showSuccess(context.$t("mapView.avoidPolygonSaved"));
        context.notifyAvoidPolygonsChanged();
      }
    },
    /**
     * Delete avoid polygon layer and run corresponding hook
     * @param {Object} polygon
     */
    deleteAvoidPolygon(polygon) {
      let context = this;
      this.getMapObject().then(map => {
        let expectedPromise = this.$root.appHooks.run("avoidPolygonRemoved", {
          polygon,
          map,
          context
        });
        // If a promise is returned
        if (expectedPromise instanceof Promise) {
          expectedPromise
            .then(() => {
              context.notifyAvoidPolygonsChanged();
            })
            .catch(err => {
              console.log(err);
            });
        } else {
          map.removeLayer(polygon);
          context.notifyAvoidPolygonsChanged();
          context.showSuccess(context.$t("mapView.avoidPolygonRemoved"));
        }
      });
    },

    /**
     * Enable polygon shape edit and run the corresponding hook
     * @param {*} polygon
     */
    enableAvoidPolygonShapeEdit(polygon) {
      polygon.editing.enable();
      polygon.closePopup();
      this.$root.appHooks.run("avoidPolygonEditModeEnabled", polygon);
      this.showInfo(this.$t("mapView.polygonEditModeEnabled"), {
        timeout: 10000
      });
    },

    /**
     * Remove all avoid polygons from map
     */
    removeAllAvoidPolygons() {
      if (this.map) {
        let map = this.map;
        map.eachLayer(function(layer) {
          if (layer instanceof Leaflet.Polygon) {
            if (lodash.get(layer, "feature.properties.avoidPolygon")) {
              map.removeLayer(layer);
            }
          }
          if (layer instanceof Leaflet.Circle) {
            // if (lodash.get(layer, 'feature.properties.avoidPolygon')) {
            map.removeLayer(layer);
            // }
          }
        });
      }
    },

    /**
     * Load all avoid polygons from the localAvoidPolygons
     */
    dengerousArea(value) {
      if(this.checkGiraStationStatus)
      {
        if(this.$store.getters.dangeruousPoints !== '5')
        {
          this.localAvoidPolygons = this.extractAvoidPolygonsFromMap(true);
          this.$emit("avoidPolygonsChanged", this.localAvoidPolygons);
        }
      }
      else {
        this.localAvoidPolygons = this.extractAvoidPolygonsFromMap(true);
        this.$emit("avoidPolygonsChanged", this.localAvoidPolygons);
      }
      // setTimeout(() => {
      //   this.eventBus.$emit('currentLocationUpdate')
      // }, 1000);

      // }
    },
    setpolygons(data) {
      // this.isDanger = !this.isDanger
      this.$emit("setpolygons", data);

      setTimeout(() => {
        // if(this.isDanger){
        // console.log(this.isDanger)
        this.localAvoidPolygons = this.extractAvoidPolygonsFromMap(true);
        this.$emit("avoidPolygonsChanged", this.localAvoidPolygons);
        // this.$emit('setpolygons',true)

        // }
        // else{
        //   // this.$emit('setpolygons',true)
        //   this.$emit('avoidPolygonsChanged', [])
        // }
      }, 1000);
    },

    //  Load all avoid polygons from the localAvoidPolygons
    loadAvoidPolygons() {
      let context = this;
      this.getMapObject().then(map => {
        context.removeAllAvoidPolygons();
        for (const key in context.localAvoidPolygons) {
          const polygonData = context.localAvoidPolygons[key];
          const coordinates = GeoUtils.switchLatLonIndex(
            polygonData.geometry.coordinates[0]
          );
          // const polygonOptions = PolygonUtils.buildPolygonOptions(polygonData, context.drawOptions.draw.polygon.shapeOptions.color)
          let polygonOptions;
          let score;
          let getpoint = localStorage.getItem("getpoint");

          this.areashow.map(data => {
            if (data.lat === Number(coordinates[0][0]) - 0.0000636) {
              score = data.marker_color;
              polygonOptions = {
                color: this.rectangleColor[data.marker_color],
                className: "hideRectangle",
                dashArray: null
              };
            }
          });
          let polygonShapeType = GeoUtils.geojsonShapeType(polygonData);
          // draw polygon 
          let polygon = PolygonUtils.createPolygon(
            coordinates,
            polygonOptions,
            polygonShapeType
          );
          // draw  Circle
          let polygons = PolygonUtils.createPolygonCircle(
            coordinates,
            polygonOptions,
            polygonShapeType
          );
          context.setAvoidPolygonProperties(polygon, polygonData);
          this.removePolygon.push(polygon.addTo(map));
          this.removeCircle.push(polygons.addTo(map));

          // Add handler for the polygon click event
          polygon.on("click", function() {
            context.onAvoidPolygonClicked(polygon, map);
          });
          polygons.on("click", function() {
            // context.onAvoidPolygonClickedcircle(polygons, map)
          });
        }
        // this.localAvoidPolygons = this.extractAvoidPolygonsFromMap(true)
        //     this.$emit('avoidPolygonsChanged', this.localAvoidPolygons)
      });
      let pixel = 20;
      if (this.zoomLevel == 18) {
        pixel = 20;
      } else if (this.zoomLevel == 17) {
        pixel = 10;
      } else if (this.zoomLevel == 16) {
        pixel = 4;
      } else if (this.zoomLevel == 15) {
        pixel = 2;
      } else if (this.zoomLevel == 14) {
        pixel = 1;
      } else if (this.zoomLevel == 13) {
        pixel = 1;
      } else {
        pixel = 0;
      }

      const collection = document.getElementsByClassName("circle-change");
      setTimeout(() => {
        // for (let i = 0; i < collection.length; i++) {
        //   collection[i].style.filter = "blur(" + pixel + "px)";
        // }
        if(this.hosportStatus || this.mobilehosportStatus) {
          if (document.getElementsByClassName("circle-change").length > 0) {
            let hostport = document.getElementsByClassName("circle-change")
              .length;
            for (let index = 0; index < hostport; index++) {
              document
                .getElementsByClassName("circle-change")
                [index].setAttribute(
                  "style",
                  "display:block;filter:blur(" + pixel + "px)"
                );
            }
          }
      }
      }, 300);

    },

    /**
     * Get all the polygons from the map object
     * @returns {Array} avoidPolygon
     */
    extractAvoidPolygonsFromMap(inGeoJsonFormat = false) {
      const mapAvoidPolygons = [];

      if (this.map) {
        // Extract each polygon from the map object
        this.map.eachLayer(function(layer) {
          if (layer instanceof Leaflet.Polygon) {
            let polygonGeoJson = layer.toGeoJSON();
            // properties defined via feature.properties in layer are accessible via
            // .properties when the layer is converted to geojson.
            if (
              polygonGeoJson.properties &&
              polygonGeoJson.properties.avoidPolygon
            ) {
              if (inGeoJsonFormat) {
                mapAvoidPolygons.push(polygonGeoJson);
              } else {
                mapAvoidPolygons.push(layer);
              }
            }
          }
        });
      }
      return mapAvoidPolygons;
    },
    /**
     * Enable edit mode for polygon by adding a edit popup when clicked
     * @param {*} polygon
     */
    onAvoidPolygonClicked(polygon, map) {
      // polygon is already in edit mode
      // So the click is used to sav the changes
      if (polygon.editing._enabled) {
        this.saveAvoidPolygonChanges(polygon, map);
      } else {
        // build the standard popup, run the popup hook and open the popup
        let popupHtmlFragment = this.buildPolygonClickPopupContent(polygon);
        this.$root.appHooks.run("beforeShowAvoidPolygonPopup", {
          popupHtmlFragment,
          polygon
        });
        polygon.bindPopup(popupHtmlFragment).openPopup();
      }
    },
    onAvoidPolygonClickedcircle(polygon, map) {
      // polygon is already in edit mode
      // So the click is used to sav the changes
      if (polygon.editing._enabled) {
        this.saveAvoidPolygonChanges(polygon, map);
      } else {
        // build the standard popup, run the popup hook and open the popup
        // let popupHtmlFragment = this.buildPolygonClickPopupContentcircle(polygon)
        // this.$root.appHooks.run('beforeShowAvoidPolygonPopup', { popupHtmlFragment, polygon })
        // polygon.bindPopup(popupHtmlFragment).openPopup()
      }
    },

    /**
     * Build polygon clicked popup content
     * @param {Object} polygon
     * @returns {DocumentFragment}
     */
    buildPolygonClickPopupContent(polygon) {
      let score;
      this.areashow.map(data => {
        if (data.lat === polygon._latlngs[0][0].lat) {
          score = data.marker_color;
        }
      });

      var popupContentWrapper = document.createElement("div");
      popupContentWrapper.className = "score";
      // let editShapeEl = document.createElement('a')
      // editShapeEl.onclick = () => {this.enableAvoidPolygonShapeEdit(polygon)}
      // editShapeEl.innerText = 'flip_to_front' // material close icon will be rendered
      // editShapeEl.title = this.$t('mapView.editShape')
      // editShapeEl.className = 'material-icons leaflet-draw-custom-actions'

      let deleteEl = document.createElement("a");
      // deleteEl.onclick = () => {this.deleteAvoidPolygon(polygon)}
      // deleteEl.innerText = 'delete' // material close icon will be rendered
      // deleteEl.title = this.$t('mapView.remove')
      deleteEl.textContent = score;
      deleteEl.className = "score_class";

      // popupContentWrapper.appendChild(editShapeEl)
      popupContentWrapper.appendChild(deleteEl);

      return popupContentWrapper;
    },
    buildPolygonClickPopupContentcircle(polygon) {
      let score;
      this.areashow.map(data => {
        if (data.lat === polygon._latlng.lat) {
          score = data.marker_color;
        }
      });

      var popupContentWrapper = document.createElement("div");
      popupContentWrapper.className = "score";
      // let editShapeEl = document.createElement('a')
      // editShapeEl.onclick = () => {this.enableAvoidPolygonShapeEdit(polygon)}
      // editShapeEl.innerText = 'flip_to_front' // material close icon will be rendered
      // editShapeEl.title = this.$t('mapView.editShape')
      // editShapeEl.className = 'material-icons leaflet-draw-custom-actions'

      let deleteEl = document.createElement("a");
      // deleteEl.onclick = () => {this.deleteAvoidPolygon(polygon)}
      // deleteEl.innerText = 'delete' // material close icon will be rendered
      // deleteEl.title = this.$t('mapView.remove')
      deleteEl.textContent = score;
      deleteEl.className = "score_class";

      // popupContentWrapper.appendChild(editShapeEl)
      popupContentWrapper.appendChild(deleteEl);

      return popupContentWrapper;
    },
    /**
     * Get all the polygons and  notify the parent component when
     * an avoid polygon is created
     * @emits avoidPolygonsChanged
     */
    notifyAvoidPolygonsChanged() {
      if (this.$refs.map.mapObject) {
        this.localAvoidPolygons = this.extractAvoidPolygonsFromMap(true);
        this.$emit("avoidPolygonsChanged", this.localAvoidPolygons);
      }
    },
    /**
     * Highlight a route point on the active route index
     * @param {*} routePointIndex
     */
    highlightRoutePoint(routePointIndex) {
      const activeRouteCoordinates = this.localMapViewData.routes[
        this.$store.getters.activeRouteIndex
      ].geometry.coordinates;
      if (activeRouteCoordinates[routePointIndex]) {
        this.highlightedRoutePointIndex = routePointIndex;
      }
    },
    /**
     * When altitude info over map close is hit
     */
    closeAltitudeInfo() {
      this.isAltitudeModalOpen = false;
      this.removeRoutePoint();
    },
    /**
     * Remove route highlight point
     */
    removeRoutePoint() {
      this.highlightedRoutePointIndex = null;
    },
    /**
     * Disable pick a place mode by
     * setting the pick pace index as null
     * @param event
     */
    disablePickPlaceMode(event) {
      if (event.which === 27) {
        // esc
        this.$store.commit("pickPlaceIndex", null);
      }
    },
    clearMap() {
      this.mapDataBuilder = null;

      // When the clearMap event is triggered, we reset places and routes
      this.localMapViewData.places = [];
      this.localMapViewData.routes = [];
      this.localMapViewData.polygons = [];
      this.clickLatlng = null;
      // this.localMapViewData.rawData = null
      // this.localMapViewData.isRouteData = false

      // console.log(this.localMapViewData)
      // let map = this.map
      // map.eachLayer(function (layer) {
      //   if (layer instanceof Leaflet.Polyline) {
      //     map.removeLayer(layer)
      //   }
      // })

      // this.$store.commit('currentLocation', null)
    },
    /**
     * When a place focus is changed (a new place is selected among the
     * ones listed on the map) updates the map center if the distance
     * between the old center and the new is greater then 50 meters
     */
    placeFocusChanged(place) {
      this.focusedPlace = place;

      const distance = GeoUtils.calculateDistanceBetweenLocations(
        this.$store.getters.mapCenter,
        place.getLatLng(),
        "m"
      );

      // We only consider that the center changed if it
      // changes more than 50 meters from the previous center
      if (distance > 50) {
        this.setFocusedPlace(place);
      }
    },
    /**
     * Highlight polyline sections on the map view by fitting the zoom to focus only the passed extra info
     * @param {*} extraInfo
     */
    highlightPolylineSections(extraInfo) {
      this.extraInfo = extraInfo;
      if (this.$store.getters.mapSettings.autoFitHighlightedBounds) {
        this.buildAndSetBounds();
        this.fit();
      }
    },
    //  geofiles () {
    //    console.log('hi')
    //   const response = geoFile;
    //   setTimeout(() => {

    //     console.log(response);
    //     this.geojson =  response;
    //     setTimeout(() => {

    //       console.log(this.geojson)
    //     }, 1000);
    //   }, 2000);
    // },
    /**
     * Add map view initial eventBus listeners
     * @listens redrawAndFitMap (via eventBus)
     * @listens clearMap (via eventBus)
     * @listens clearMap (via eventBus)
     * @listens placeFocusChanged (via eventBus)
     * @listens changeActiveRouteIndex (via eventBus)
     * @listens altitudeChartHoverIndexChanged (via eventBus)
     * @listens mouseLeftChartAltitudeChart (via eventBus)
     * @listens showAltitudeModal (via eventBus)
     * @listens highlightPolylineSections (via eventBus)
     */

    // set select dengerous point
    setValuePolygones(value) {
      if (value.value == "2") {
        this.routeSafety = "Fastest";
      } else if (value.value == "3") {
        this.routeSafety = "Balanced";
      } else if (value.value == "4") {
        this.routeSafety = "Safest";
      }
      this.routeSafetyno = value;
      this.$emit("polygonvaluecheck", value);
    },
    // marker  draw when user select form feild
    setmarkers(data) {
      if (this.setmarkerLalLng !== null) {
        this.map.removeLayer(this.setmarkerLalLng);
      }
      if (data.placeName !== "Your location") {
        this.setmarker = true;
        this.setmarkerLalLng = [data.lat, data.lng];
        const latlng = GeoUtils.buildLatLong(data.lat, data.lng);
        let markers = GeoUtils.buildMarkers([data]);
        markers = Leaflet.marker(
          [markers[0].position.lat, markers[0].position.lng],
          {
            icon: markers[0].icon
          }
        );
        markers.addTo(this.map);
        this.setmarkerLalLng = markers;

        setTimeout(() => {
          this.setMapCenter(latlng);
        }, 800);
      } else {
        this.setmarker = false;
        this.setmarkerLalLng = [data.lat, data.lng];
        const latlng = GeoUtils.buildLatLong(data.lat, data.lng);
        let markers = GeoUtils.buildMarkers([data]);
        markers = Leaflet.marker(
          [markers[0].position.lat, markers[0].position.lng],
          {
            icon: markers[0].icon
          }
        );
        markers.addTo(this.map);
        this.setmarkerLalLng = markers;

        setTimeout(() => {
          this.setMapCenter(latlng);
        }, 800);
        this.$store.commit("currentLocation", data);
        this.myLocationActive = true;
        // this.updateMyLocationFirstTime(true)
      }
    },
    // marker  draw when user select TO feild
    setmarkerfirst(data) {
      if (this.secendFeilds !== null) {
        this.map.removeLayer(this.secendFeilds);
      }
      const latlng = GeoUtils.buildLatLong(data.lat, data.lng);
      let markers;
      if(this.$store.getters.getMapstatus) {
        markers = GeoUtils.buildMarkersPlaceName([data],'black');
      }
      else {
        markers = GeoUtils.buildMarkersPlaceName([data],'white');
      }
      markers = Leaflet.marker(
        [markers[0].position.lat, markers[0].position.lng],
        {
          icon: markers[0].icon
        }
      );
      markers.addTo(this.map);
      this.secendFeilds = markers;

      setTimeout(() => {
        this.setMapCenter(latlng);
      }, 800);
    },

    // map center
    centerMapFouseTextfield (data) {
      if(data !== null) {
        const latlng = GeoUtils.buildLatLong(data.lat, data.lng);
        setTimeout(() => {
          this.setMapCenter(latlng);
        }, 200);
      }
    },

    //remove marker 
    removemarkersecond() {
      this.map.removeLayer(this.setmarkerLalLng);
      if (this.RoutePlaceSecondRemove !== null) {
        this.map.removeLayer(this.RoutePlaceSecondRemove);
        this.routeSafetyTime = false;
        this.polilineDraw = false;
      }
    },
    //remove marker 
    removemarkerfirst() {
      this.map.removeLayer(this.secendFeilds);
      if (this.RoutePlaceOneRemove !== null) {
        this.map.removeLayer(this.RoutePlaceOneRemove);
        this.routeSafetyTime = false;
        this.polilineDraw = false;
      }
    },

    setmarkerFalse() {
      this.setmarker = false;
    },
    // remove all hostpot
    removeHostport () {
      this.removeHostportMarker && this.removeHostportMarker.map(data => {
        this.map.removeLayer(data);
      });
      this.removeAddHostpot && this.removeAddHostpot.map(data => {
          this.map.removeLayer(data);
      });

    // add hostpot
    },
    addHostport() {
      this.hostports && this.hostports.map(data => {
        var popupContentWrapper = document.createElement("div");
        var popupContent = document.createElement("div");
        popupContentWrapper.className = "score";
        let editShapeEl = document.createElement("p");
        let dateShapeEl = document.createElement("p");
        editShapeEl.className="description-hostport"
        dateShapeEl.className = "date-hostpost"
        editShapeEl.textContent =  data.place.description;
        if(data.place.date_added !== null) {
          dateShapeEl.textContent = data.place.date_added
        }

        let deleteEl = document.createElement("button");
        let solvedEl = document.createElement("button");
        let storeData = localStorage.getItem("storeVoteId");
        let jsonparse = JSON.parse(storeData);
        let solvedVoteId = localStorage.getItem("solvedVoteId");
        solvedVoteId = JSON.parse(solvedVoteId);
        if (jsonparse !== null && jsonparse.includes(data.place.id)) {
          deleteEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_up</i><span>Vote</span>';
          deleteEl.className = "vote-button-disable ";
          deleteEl.disabled = true;
        } else {
          deleteEl.onclick = () => {
            this.hosportmarkersVote(data.place.id);
          };
          deleteEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_up</i><span>Vote</span>';
          deleteEl.className = "vote-button";
        }
        if (
          solvedVoteId !== null &&
          solvedVoteId.includes(data.place.id)
        ) {
          solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
          solvedEl.className = "solved-button-disable";
        } else {
          if(data.place.no_votes !== 0) {
            solvedEl.onclick = () => {
              this.hosportmarkersSolved(data.place.id);
            };
            solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
            solvedEl.className = "solved-button";
          } 
          else {
            solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
            solvedEl.className = "solved-button-disable";
          }
        }
        popupContent.className = 'tooltip-buttons'
        popupContent.appendChild(deleteEl);
        popupContent.appendChild(solvedEl);
        popupContentWrapper.appendChild(editShapeEl);
        popupContentWrapper.append(dateShapeEl)
        popupContentWrapper.appendChild(popupContent);
        // popupContentWrapper.appendChild(solvedEl);
        let polygons = Leaflet.marker(
          [data.position.lat, data.position.lng],
          { icon: data.icon }
        ).bindPopup(popupContentWrapper, {
          autoClose: true,
          closeOnClick: true,
          autoPan: false
        });
        const context = this
        polygons.on("click", function() {
          const latlng = GeoUtils.buildLatLong(polygons._latlng.lat, polygons._latlng.lng);
            context.setMapCenter(latlng);            
        });
        this.removeHostportMarker.push(polygons.addTo(this.map));
      });
      this.addHostpot && this.addHostpot.map(data => {
        var popupContentWrapper = document.createElement("div");
        var popupContent = document.createElement("div");
        popupContentWrapper.className = "score"
        popupContent.className = 'tooltip-buttons'
        let editShapeEl = document.createElement("p");
        let deleteEl = document.createElement("button");
        let solvedEl = document.createElement("button");
        editShapeEl.className="description-hostport"
        // dateShapeEl.className = "date-hostpost"
        let storeData = localStorage.getItem("storeVoteId");
        let jsonparse = JSON.parse(storeData);
        let solvedVoteId = localStorage.getItem("solvedVoteId");
        solvedVoteId = JSON.parse(solvedVoteId);
        if (jsonparse !== null && jsonparse.includes(data.place.id)) {
          deleteEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_up</i><span>Vote</span>';
          deleteEl.className = "vote-button-disable ";
          deleteEl.disabled = true;
        } else {
          // deleteEl.onclick = () => {
          //   this.hosportmarkersVote(data.place.id);
          // };
          deleteEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_up</i><span>Vote</span>';
          deleteEl.className = "vote-button";
        }
        if (
          solvedVoteId !== null &&
          solvedVoteId.includes(data.place.id)
        ) {
          solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
          solvedEl.className = "solved-button-disable";
        } else {
          if(data.place.no_votes !== 0) {
            // solvedEl.onclick = () => {
            //   this.hosportmarkersSolved(data.place.id);
            // };
            solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
            solvedEl.className = "solved-button";
          } 
          else {
            solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
            solvedEl.className = "solved-button-disable";
          }
        }
          // editShapeEl.textContent = data.place.description
          editShapeEl.textContent =  data.place.date_added !== null ? data.place.description + " - "  + data.place.date_added :data.place.description;
          let dateShapeEl = document.createElement("p");
          dateShapeEl.className = "add-date-hostpost text-a-center"
          if(data.place.date_added !== null) {
            dateShapeEl.textContent = data.place.date_added
          }
          popupContent.appendChild(deleteEl);
          popupContent.appendChild(solvedEl);
          popupContentWrapper.appendChild(editShapeEl);
          popupContentWrapper.appendChild(popupContent);
        let polygons = Leaflet.marker(
          [data.position.lat, data.position.lng],
          { icon: data.icon }
        ).bindPopup(popupContentWrapper, {
          autoClose: true,
          closeOnClick: true,
          autoPan: false
        });
        const context = this
            polygons.on("click", function() {
              const latlng = GeoUtils.buildLatLong(polygons._latlng.lat, polygons._latlng.lng);
                context.setMapCenter(latlng);            
            });
        this.removeAddHostpot.push(polygons.addTo(this.map))
      })
      let hosport = document.getElementsByClassName("custom-div-icon")
              .length;
            if (hosport > 0) {
              for (let index = 0; index < hosport; index++) {
                document.getElementsByClassName("custom-div-icon")[
                  index
                ].firstChild.className = "custom-html-icon-div hosport-marker";
              }
            }
      if (
        document.getElementsByClassName("hosport-marker").length > 0
      ) {
        let hostport = document.getElementsByClassName("hosport-marker")
          .length;
        for (let index = 0; index < hostport; index++) {
          document
            .getElementsByClassName("hosport-marker")
            [index].setAttribute(
              "style",
              "visibility:visible !important;cursor:pointer;"
            );
        }
      }
    },
    // hostpot API
    hosportMarkers(hosportCheck) {
      fetch(this.baseurl + "api/getHotspots", {
        method: "GET",
        // Access-Control-Allow-Origin: "*",
        async:false,
        mode: "cors",
        headers: { "Content-type": "application/json;charset=UTF-8" }
      })
        .then(response => response.json())
        .then(data => {
          this.eventBus.$emit("HostportArrayData", data);
          this.removeHostportMarker &&
            this.removeHostportMarker.map(data => {
              this.map.removeLayer(data);
            });
          let markers = GeoUtils.buildMarkersHosport(data);
          markers = this.$root.appHooks.run("markersCreated", markers);
          this.hostports = markers;
          if(!this.oneTimeAPICallHostpot) {
            let accdentData = this.$store.getters.AccidentHostpot
            this.$store.commit('AccidentHostpot',[...accdentData,  ...markers])
          }
          this.oneTimeAPICallHostpot = true;
          if (!this.$lowResolution) {
            this.hostports && this.hostports.map(data => {
              var popupContentWrapper = document.createElement("div");
              var popupContent = document.createElement("div");
              popupContentWrapper.className = "score";
              let editShapeEl = document.createElement("p");
              let dateShapeEl = document.createElement("p");
              dateShapeEl.className = "date-hostpost"
              // editShapeEl.onclick = () => {this.enableAvoidPolygonShapeEdit(polygon)}
              // editShapeEl.innerText = "flip_to_front"; // material close icon will be rendered
              // editShapeEl.title = this.$t('mapView.editShape')
              // editShapeEl.className =
              //   "material-icons leaflet-draw-custom-actions";
              editShapeEl.textContent = data.place.description;
              if(data.place.date_added !== null) {
                dateShapeEl.textContent = data.place.date_added
              }

              let deleteEl = document.createElement("button");
              let solvedEl = document.createElement("button");
              let storeData = localStorage.getItem("storeVoteId");
              let jsonparse = JSON.parse(storeData);
              let solvedVoteId = localStorage.getItem("solvedVoteId");
              solvedVoteId = JSON.parse(solvedVoteId);
              if (jsonparse !== null && jsonparse.includes(data.place.id)) {
                // deleteEl.innerText = 'delete' // material close icon will be rendered
                // deleteEl.title = this.$t('mapView.remove')
                // deleteEl.textContent = "Vote";
                deleteEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_up</i><span>Vote</span>';
                deleteEl.className = "vote-button-disable ";
                deleteEl.disabled = true;
                // if (
                //   solvedVoteId !== null &&
                //   solvedVoteId.includes(data.place.id)
                // ) {
                //   solvedEl.textContent = "Solved";
                //   solvedEl.className = "solved-button-disable";
                // } else {
                //   solvedEl.onclick = () => {
                //     this.hosportmarkersSolved(data.place.id);
                //   };
                //   solvedEl.textContent = "Solved";
                //   solvedEl.className = "solved-button";
                // }
              } else {
                deleteEl.onclick = () => {
                  this.hosportmarkersVote(data.place.id);
                };
                // deleteEl.innerText = 'delete' // material close icon will be rendered
                // deleteEl.title = this.$t('mapView.remove')
                // deleteEl.textContent = "Vote";
                deleteEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_up</i><span>Vote</span>';
                // deleteEl.innerText = 'delete';
                deleteEl.className = "vote-button";
              }
              if (
                solvedVoteId !== null &&
                solvedVoteId.includes(data.place.id)
              ) {
                // solvedEl.textContent = "Solved";
                solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
                solvedEl.className = "solved-button-disable";
              } else {
                if(data.place.no_votes !== 0) {
                  solvedEl.onclick = () => {
                    this.hosportmarkersSolved(data.place.id);
                  };
                  // solvedEl.textContent = "Solved";
                  solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
                  solvedEl.className = "solved-button";
                } 
                else {
                  // solvedEl.textContent = "Solved";
                  solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
                  solvedEl.className = "solved-button-disable";
                }
              }
              popupContent.className = 'tooltip-buttons'
              popupContent.appendChild(deleteEl);
              popupContent.appendChild(solvedEl);
              popupContentWrapper.appendChild(editShapeEl);
              popupContentWrapper.append(dateShapeEl)
              popupContentWrapper.appendChild(popupContent);
              // popupContentWrapper.appendChild(solvedEl);
              let polygons = Leaflet.marker(
                [data.position.lat, data.position.lng],
                { icon: data.icon }
              ).bindPopup(popupContentWrapper, {
                autoClose: true,
                closeOnClick: true,
                autoPan: false
              });
              const context = this
              polygons.on("click", function() {
                const latlng = GeoUtils.buildLatLong(polygons._latlng.lat, polygons._latlng.lng);
                  context.setMapCenter(latlng);            
              });
              this.removeHostportMarker.push(polygons.addTo(this.map));
            });
          }
          
          setTimeout(() => {
            let hosport = document.getElementsByClassName("custom-div-icon")
              .length;
            if (hosport > 0) {
              for (let index = 0; index < hosport; index++) {
                document.getElementsByClassName("custom-div-icon")[
                  index
                ].firstChild.className = "custom-html-icon-div hosport-marker";
              }
            }

            if (this.hosportStatus || this.mobilehosportStatus) {
              // if (
              //   document.getElementsByClassName("hosport-marker").length > 0
              // ) {
              //   let hostport = document.getElementsByClassName("hosport-marker")
              //     .length;
              //   for (let index = 0; index < hostport; index++) {
              //     document
              //       .getElementsByClassName("hosport-marker")
              //       [index].setAttribute(
              //         "style",
              //         " visibility:visible !important;cursor:pointer;"
              //       );
              //   }
              // }
              this.addHostport();
            }
            if (!this.$lowResolution) {
              setTimeout(() => {
                if(hosportCheck === true ) {
                  
                }
                else {
                  document.getElementById("hosportIdMobile").click(); 
                }
                if(this.hosportStatus || this.mobilehosportStatus) {
                  if (document.getElementsByClassName("circle-change").length > 0) {
                    let hostport = document.getElementsByClassName("circle-change")
                      .length;
                    for (let index = 0; index < hostport; index++) {
                      document
                        .getElementsByClassName("circle-change")
                        [index].setAttribute(
                          "style",
                          "display:block;"
                        );
                    }
                  }
              }
              }, 300);
            }
            else {
              const checkdirection = url.includes("hotspotMap");
              if (checkdirection) {
                document.getElementById("MobileHostpot").click();
                setTimeout(() => {
                  document.getElementsByClassName("mobile-insert")[0].click();
                }, 100);
              }
            }
          }, 100);
        });
        
    },
    // accident hostpot API
    addMoreHostMarker() {
      fetch(this.baseurl + "api/getAccidentsLondon?severity=3", {
        method: "GET",
        // Access-Control-Allow-Origin: "*",
        async:false,
        mode: "cors",
        headers: { "Content-type": "application/json;charset=UTF-8" }
      })
        .then(response => response.json())
        .then(data => {
          let markers = GeoUtils.buildMarkersHosport(data);
          this.addHostpot = markers 
          if(!this.oneTimeAPICallAccident) {
            let accdentData = this.$store.getters.AccidentHostpot
            this.$store.commit('AccidentHostpot',[...accdentData,  ...markers])
          }
          this.oneTimeAPICallAccident = true
          this.removeAddHostpot &&
            this.removeAddHostpot.map(data => {
              this.map.removeLayer(data);
            });
          this.addHostpot && this.addHostpot.map(data => {
            var popupContentWrapper = document.createElement("div");
            var popupContent = document.createElement("div");
            popupContentWrapper.className = "score"
            popupContent.className = 'tooltip-buttons'
            let editShapeEl = document.createElement("p");
            let deleteEl = document.createElement("button");
            let solvedEl = document.createElement("button");
            editShapeEl.className="description-hostport"
            // dateShapeEl.className = "date-hostpost"
            let storeData = localStorage.getItem("storeVoteId");
            let jsonparse = JSON.parse(storeData);
            let solvedVoteId = localStorage.getItem("solvedVoteId");
            solvedVoteId = JSON.parse(solvedVoteId);
            if (jsonparse !== null && jsonparse.includes(data.place.id)) {
              deleteEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_up</i><span>Vote</span>';
              deleteEl.className = "vote-button-disable ";
              deleteEl.disabled = true;
            } else {
              // deleteEl.onclick = () => {
              //   this.hosportmarkersVote(data.place.id);
              // };
              deleteEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_up</i><span>Vote</span>';
              deleteEl.className = "vote-button";
            }
            if (
              solvedVoteId !== null &&
              solvedVoteId.includes(data.place.id)
            ) {
              solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
              solvedEl.className = "solved-button-disable";
            } else {
              if(data.place.no_votes !== 0) {
                // solvedEl.onclick = () => {
                //   this.hosportmarkersSolved(data.place.id);
                // };
                solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
                solvedEl.className = "solved-button";
              } 
              else {
                solvedEl.innerHTML = '<i  aria-hidden="true" class="v-icon material-icons theme--black">keyboard_arrow_down</i><span>Solved</span>';
                solvedEl.className = "solved-button-disable";
              }
            }
              // editShapeEl.textContent = data.place.description
              editShapeEl.textContent =  data.place.date_added !== null ? data.place.description + " - "  + data.place.date_added :data.place.description;
              let dateShapeEl = document.createElement("p");
              dateShapeEl.className = "add-date-hostpost text-a-center"
              if(data.place.date_added !== null) {
                dateShapeEl.textContent = data.place.date_added
              }
              popupContent.appendChild(deleteEl);
              popupContent.appendChild(solvedEl);
              popupContentWrapper.appendChild(editShapeEl);
              popupContentWrapper.appendChild(popupContent);
            let polygons = Leaflet.marker(
              [data.position.lat, data.position.lng],
              { icon: data.icon }
            ).bindPopup(popupContentWrapper, {
              autoClose: true,
              closeOnClick: true,
              autoPan: false
            });
            const context = this
            polygons.on("click", function() {
              const latlng = GeoUtils.buildLatLong(polygons._latlng.lat, polygons._latlng.lng);
                context.setMapCenter(latlng);            
            });
            this.removeAddHostpot.push(polygons.addTo(this.map))
          })
          setTimeout(() => {
            let hosport = document.getElementsByClassName("custom-div-icon")
              .length;
            if (hosport > 0) {
              for (let index = 0; index < hosport; index++) {
                document.getElementsByClassName("custom-div-icon")[
                  index
                ].firstChild.className = "custom-html-icon-div hosport-marker";
              }
            }

            if (this.hosportStatus || this.mobilehosportStatus) {
              if (
                document.getElementsByClassName("hosport-marker").length > 0
              ) {
                let hostport = document.getElementsByClassName("hosport-marker")
                  .length;
                for (let index = 0; index < hostport; index++) {
                  document
                    .getElementsByClassName("hosport-marker")
                    [index].setAttribute(
                      "style",
                      " visibility:visible !important;cursor:pointer;"
                    );
                }
              }
            }
            // if (!this.$lowResolution) {
            //   setTimeout(() => {
            //     if(hosportCheck === true ) {

            //     }
            //     else {
            //       document.getElementById("hosportIdMobile").click(); 
            //     }
            //     if(this.hosportStatus || this.mobilehosportStatus) {
            //       if (document.getElementsByClassName("circle-change").length > 0) {
            //         let hostport = document.getElementsByClassName("circle-change")
            //           .length;
            //         for (let index = 0; index < hostport; index++) {
            //           document
            //             .getElementsByClassName("circle-change")
            //             [index].setAttribute(
            //               "style",
            //               "display:block;"
            //             );
            //         }
            //       }
            //   }
            //   }, 300);
            // }
          }, 100);

        })

    },
    // hostpot solved api
    hosportmarkersSolved(id) {
      let storeData = localStorage.getItem("solvedVoteId");

      if (storeData === null) {
        localStorage.setItem("solvedVoteId", JSON.stringify(this.a));
        storeData = localStorage.getItem("solvedVoteId");
        let jsonparse = JSON.parse(storeData);
        jsonparse.push(id);
        localStorage.setItem("solvedVoteId", JSON.stringify(jsonparse));
        fetch(this.baseurl + "api/downVoteHotspot?id=" + id, {
          method: "GET",
          headers: {
            "Content-type": "application/json;charset=UTF-8"
          }
        })
          .then(response => response.json())
          .then(data => {
        this.hosportMarkers(true);
          });
      } else {
        let jsonparse = JSON.parse(storeData);
        if (!jsonparse.includes(id)) {
          jsonparse.push(id);
          localStorage.setItem("solvedVoteId", JSON.stringify(jsonparse));
          fetch(this.baseurl + "/api/downVoteHotspot?id=" + id, {
            method: "GET",
            headers: {
              "Content-type": "application/json;charset=UTF-8"
            }
          })
            .then(response => response.json())
            .then(data => {
          this.hosportMarkers(true);
            });
        }
      }
    },
    // hostpot vote api
    hosportmarkersVote(id) {
      let storeData = localStorage.getItem("storeVoteId");

      if (storeData === null) {
        localStorage.setItem("storeVoteId", JSON.stringify(this.a));
        storeData = localStorage.getItem("storeVoteId");
        let jsonparse = JSON.parse(storeData);
        jsonparse.push(id);
        localStorage.setItem("storeVoteId", JSON.stringify(jsonparse));
        fetch(this.baseurl + "api/upVoteHotspot?id=" + id, {
          method: "GET",
          headers: {
            "Content-type": "application/json;charset=UTF-8"
          }
        })
          .then(response => response.json())
          .then(data => {
            this.hosportMarkers(true);
          });
      } else {
        let jsonparse = JSON.parse(storeData);
        if (!jsonparse.includes(id)) {
          jsonparse.push(id);
          localStorage.setItem("storeVoteId", JSON.stringify(jsonparse));
          fetch(this.baseurl + "/api/upVoteHotspot?id=" + id, {
            method: "GET",
            headers: {
              "Content-type": "application/json;charset=UTF-8"
            }
          })
            .then(response => response.json())
            .then(data => {
              this.hosportMarkers(true);
            });
        }
      }
    },
    // remove gira station
    removeGira () {
      this.removeGiraMarker && this.removeGiraMarker.map(data => {
        this.map.removeLayer(data);
      });
    },
    // add gira station
    addGira () {
      this.giraMarker &&this.giraMarker.map(data => {
        var popupContentWrapper = document.createElement("div");
        popupContentWrapper.className="gira-cycle-number"
        popupContentWrapper.innerHTML ="<p class='mb-0'>"+ data.place.properties.num_bicicletas + "/" + data.place.properties.num_docas + " bicycles</p>";
      
        let polygongira = Leaflet.marker(
          [data.position.lat, data.position.lng],
          { icon: data.icon }
        )
           .bindPopup(popupContentWrapper, {
                autoClose: true,
                closeOnClick: true,
                autoPan: false
              });
              const context = this
              polygongira.on("click", function() {
                const latlng = GeoUtils.buildLatLong(polygongira._latlng.lat, polygongira._latlng.lng);
                  context.setMapCenter(latlng);            
              });
        this.removeGiraMarker.push(polygongira.addTo(this.map));
        setTimeout(() => {
          let gira = document.getElementsByClassName("custom-gira-icon").length;
          if (gira > 0) {
            for (let index = 0; index < gira; index++) {
              document.getElementsByClassName("custom-gira-icon")[
              index
              ].firstChild.className = "custom-html-icon-div gira-marker";
            }
          }
        }, 50);
      });
    },
    // get gira station  API
    giraMarkers() {
      fetch(this.baseurl + "api/getGiraStations", {
        method: "GET",
        headers: {
          "Access-Control-Allow-Origin": "*",
          connection: "Keep-Alive",
          "Proxy-Connection": "Keep-Alive",
          "Content-type": "application/json;charset=UTF-8",
          api_key: "db8740c6db5b392b2f18652791f20eea"
        }
      })
        .then(response => response.json())
        .then(data => {
          let markers = GeoUtils.buildMarkersGira(data.features);
          // markers = this.$root.appHooks.run("markersCreated", markers);
          this.giraMarker = markers;
          this.giraMarker &&
            this.giraMarker.map(data => {
              let polygongira = Leaflet.marker(
                [data.position.lat, data.position.lng],
                { icon: data.icon }
              );
              // .bindPopup("", {
              //   autoClose: true,
              //   closeOnClick: true,
              //   autoPan: false
              // });
            //  this.removeGiraMarker.push(polygongira.addTo(this.map));
              // polygons.on('click', function () {
              //   context.onAvoidPolygonClickedcircle(polygons, map)
              // })
            });
          const checkdirection = url.includes("gira");
          if (checkdirection) {
            if (this.$lowResolution) {
              setTimeout(() => {
                document.getElementById("giraSwitchMobile").click();
              }, 500);
            } else {
              setTimeout(() => {
                document.getElementById("giraSwitch").click();
              }, 500);
            }
          }
          // var routingControl = Leaflet.Routing.Control({
          //   waypoints: [
          //     Leaflet.latLng(57.74, 11.94),
          //     Leaflet.latLng(57.6792, 11.949)
          //   ],
          //   show: true
          // }).addTo(this.map);
        });
    },
    // gira station route API
    routeDrawone(start, end) {
      fetch(
        "https://api.openrouteservice.org/v2/directions/foot-walking?api_key=5b3ce3597851110001cf62485eefdb845a8c4441b69f9b6f76d13e1c&start=" +
          start.lng.toFixed(6) +
          "," +
          start.lat.toFixed(6) +
          "&end=" +
          end.position.lng +
          "," +
          end.position.lat,
        {
          method: "GET"
        }
      )
        .then(response => response.json())
        .then(data => {
          if(data.features[0].properties.summary !== {}) {
              this.oneDuration = data.features[0].properties.summary.duration + this.secondDuration
              this.oneDistance = data.features[0].properties.summary.distance + this.secondDistance
          }
          if (this.removeGiraStationone !== null) {
            this.map.removeLayer(this.removeGiraStationone);
          }
          let giraStatonsRoute = GeoUtils.switchLatLonIndex(
            data.features[0].geometry.coordinates
          );
          this.girastationone = giraStatonsRoute;
          this.removeGiraStationone = Leaflet.polyline(giraStatonsRoute, {
            className: "gira_station_route",
            color: "#e26d00",
            dashArray: "5,16",
            weight: "10",
            opacity: 0.9
          }).addTo(this.map);
        });
    },
    // gira route API
    routeDrawsecend(start, end) {
      fetch(
        "https://api.openrouteservice.org/v2/directions/foot-walking?api_key=5b3ce3597851110001cf62485eefdb845a8c4441b69f9b6f76d13e1c&start=" +
          start.lng.toFixed(6) +
          "," +
          start.lat.toFixed(6) +
          "&end=" +
          end.position.lng +
          "," +
          end.position.lat,
        {
          method: "GET"
        }
      )
        .then(response => response.json())
        .then(data => {
          if(data.features[0].properties.summary !== {}) {
            this.secondDuration = data.features[0].properties.summary.duration + this.oneDuration
            this.secondDistance = data.features[0].properties.summary.distance + this.oneDistance
        }
          if (this.removeGiraStationsecend !== null) {
            this.map.removeLayer(this.removeGiraStationsecend);
          }
          let giraStatonsRoute = GeoUtils.switchLatLonIndex(
            data.features[0].geometry.coordinates
          );
          this.girastationsecond = giraStatonsRoute;
          this.removeGiraStationsecend = Leaflet.polyline(giraStatonsRoute, {
            className: "gira_station_route",
            color: "#e26d00",
            dashArray: "5,16",
            weight: "10",
            opacity: 0.9
          }).addTo(this.map);
        });
    },
    //gira station route draw
    giraStationOne(value) {
      this.dangerponitfirst = value;
      let distanceArray = [];
      let numOfBike = [];
      this.giraMarker &&
        this.giraMarker.map(data => {
          if (data.place.properties.num_bicicletas != 0) {
            numOfBike.push(data);
            let distances = GeoUtils.calculateDistanceBetweenLocations(
              value,
              { lat: data.position.lat, lng: data.position.lng },
              "Km"
            );
            distanceArray.push(distances);
          }
          // if (data.place.properties.num_bicicletas != 0) {
          // }
        });
      let keyGet;
      distanceArray &&
        distanceArray.map((data, i) => {
          if (data === Math.min(...distanceArray)) {
            keyGet = i;
          }
        });
      this.getDangerPointCount();
      this.removeRouteDrawMarker();
      let places = new Place(
        numOfBike[keyGet].position.lng,
        numOfBike[keyGet].position.lat,
        this.RoutePlaceOne.placeName
      );
      this.giraRoutePlaceOne = places;
      if (this.checkGiraStationStatus) {
        this.eventBus.$emit("GoogleAPI", places, 0);
      }
      this.routeDrawone(value, numOfBike[keyGet]);
    },
    //gira station route draw
    giraStationSecond(value) {
      this.dangerponitsecend = value;
      let distanceArray = [];
      let numOfBike = [];
      this.giraMarker &&
        this.giraMarker.map(data => {
          if (data.place.properties.num_bicicletas != 0) {
            numOfBike.push(data);
            let distances = GeoUtils.calculateDistanceBetweenLocations(
              value,
              { lat: data.position.lat, lng: data.position.lng },
              "Km"
            );
            distanceArray.push(distances);
          }
        });
      let keyGet;
      distanceArray &&
        distanceArray.map((data, i) => {
          if (data === Math.min(...distanceArray)) {
            keyGet = i;
          }
        });
      this.getDangerPointCount();
      this.removeRouteDrawMarker();
      let places = new Place(
        numOfBike[keyGet].position.lng,
        numOfBike[keyGet].position.lat,
        this.RoutePlaceSecond.placeName
      );
      this.giraRoutePlaceSecond = places;
      if (this.checkGiraStationStatus) {
        this.eventBus.$emit("GoogleAPI", places, 1);
      }
      this.routeDrawsecend(value, numOfBike[keyGet]);
    },
    //dengerous point count
    getDangerPointCount() {
      if (this.dangerponitfirst !== null && this.dangerponitsecend !== null) {
        fetch(
          "https://api.openrouteservice.org/v2/directions/cycling-regular?api_key=5b3ce3597851110001cf62485eefdb845a8c4441b69f9b6f76d13e1c&start=" +
            this.dangerponitfirst.lng.toFixed(6) +
            "," +
            this.dangerponitfirst.lat.toFixed(6) +
            "&end=" +
            this.dangerponitsecend.lng +
            "," +
            this.dangerponitsecend.lat,
          {
            method: "GET"
          }
        )
          .then(response => response.json())
          .then(data => {
            let count;
            let distance = 180;
            var distances = [];
            var matchingPoint = [];

            // let giraStatonsRoute = GeoUtils.switchLatLonIndex(
            //   data.features[0].geometry.coordinates
            // );
            // this.girastationone = giraStatonsRoute;
            // Leaflet.polyline(giraStatonsRoute, {
            //   className: "gira_station_route_",
            //   color: "white"
            // }).addTo(this.map);

            data &&
              data.features[0].geometry.coordinates &&
              data.features[0].geometry.coordinates.forEach((coordinate, i) => {
                if (i < data.features[0].geometry.coordinates.length - 1) {
                  var distance = GeoUtils.calculateDistanceBetweenLocations(
                    {
                      lat: coordinate[1],
                      lng: coordinate[0]
                    },
                    {
                      lat: data.features[0].geometry.coordinates[i + 1][1],
                      lng: data.features[0].geometry.coordinates[i + 1][0]
                    },
                    "m"
                  );
                  var point0 = new google.maps.LatLng(
                    coordinate[1],
                    coordinate[0]
                  );
                  var point1 = new google.maps.LatLng(
                    data.features[0].geometry.coordinates[i + 1][1],
                    data.features[0].geometry.coordinates[i + 1][0]
                  );
                  var spherical = google.maps.geometry.spherical;
                  var p1 = spherical.computeOffset(point0, distance, 0);
                  var p2 = spherical.computeOffset(point0, distance, 90);
                  var p3 = spherical.computeOffset(point0, distance, 180);
                  var p4 = spherical.computeOffset(point0, distance, 270);

                  let ele = DengerousPoints.data.filter(element => {
                    if (element.marker_color <= this.routeSafetyno.value) {
                      let isInside =
                        Number(element.lat) > p3.lat() &&
                        Number(element.lat) < p1.lat() &&
                        Number(element.lon) < p2.lng() &&
                        Number(element.lon) > p4.lng();

                      let isNear = false;
                      let alreadyMatched = matchingPoint.indexOf(element) > -1;
                      if (isInside && !alreadyMatched) {
                        let distance0 = GeoUtils.calculateDistanceBetweenLocations(
                          {
                            lat: point0.lat(),
                            lng: point0.lng()
                          },
                          {
                            lat: Number(element.lat),
                            lng: Number(element.lon)
                          },
                          "m"
                        );
                        let distance1 = GeoUtils.calculateDistanceBetweenLocations(
                          {
                            lat: point1.lat(),
                            lng: point1.lng()
                          },
                          {
                            lat: Number(element.lat),
                            lng: Number(element.lon)
                          },
                          "m"
                        );

                        if (distance0 + distance1 <= distance + 0.34) {
                          isNear = true;
                          console.log("isNear: ", distance);
                        }
                      }
                      // console.log(
                      //   "filter data",
                      //   "isInside: " +
                      //     isInside +
                      //     "isNear: " +
                      //     isNear +
                      //     "isInMatchingPoint: " +
                      //     alreadyMatched
                      // );
                      if (isInside && isNear) return element;
                    }
                  });
                  if (ele.length > 0) {
                    // let olygonOptions = {
                    //   color: "red",
                    //   fill: "#0000",
                    //   className: "23321",
                    //   dashArray: null
                    // };

                    // Leaflet.polygon(
                    //   [
                    //     [p1.lat(), p1.lng()],
                    //     [p2.lat(), p2.lng()],
                    //     [p3.lat(), p3.lng()],
                    //     [p4.lat(), p4.lng()]
                    //   ],
                    //   olygonOptions
                    // ).addTo(this.map);

                    // matchingPoint = matchingPoint.concat(ele);
                    matchingPoint = [...matchingPoint, ...ele];
                    // console.log("element pushed: " + ele);
                  }
                }
              });
            this.countDengerousPoint = matchingPoint
            console.log("matchingPoint Length", matchingPoint);

            // var pointA = new google.maps.LatLng(
            //   23.04542001767247,
            //   72.51191230844492
            // );
            // var distancess = 100; // 10 metres
            // var bearing = 90; // 90 degrees
            // var point = new google.maps.LatLng(55.623151, 8.48215);
            // var spherical = google.maps.geometry.spherical;
            // var north = spherical.computeOffset(pointA, 100, 0);
            // var west = spherical.computeOffset(pointA, 100, -90);
            // var south = spherical.computeOffset(pointA, 100, 180);
            // var east = spherical.computeOffset(pointA, 100, 90);
            // console.log("North Lat: " + north.lat() + " Long: " + north.lng());
            // console.log("West Lat: " + west.lat() + " Long: " + west.lng());
            // console.log("East Lat: " + east.lat() + " Long: " + east.lng());
            // console.log("South Lat: " + south.lat() + " Long: " + south.lng());
          });
      }
    },
    arePointsNear(checkPoint, centerPoint, km) {
      // console.log(checkPoint);
      // console.log(centerPoint);
      var ky = 40000 / 360;
      var kx = Math.cos((Math.PI * centerPoint.lat) / 180.0) * ky;
      var dx = Math.abs(centerPoint.lng - Number(checkPoint.lon)) * kx;
      var dy = Math.abs(centerPoint.lat - Number(checkPoint.lat)) * ky;
      // console.log(dx);
      console.log(Math.sqrt(dx * dx + dy * dy) * 1000);
      return Math.sqrt(dx * dx + dy * dy) / 1000 <= km;
    },
    // intert Hostpot api
    inserthostport() {
      if (this.queAns !== null && this.queAns.length > 4) {
        this.isqueModal = false;
        fetch(
          this.baseurl +
            "/api/insertHotspot?name=" +
            this.addressGetWhenClickMap.properties.name +
            "&latitude=" +
            this.addressGetWhenClickMap.lat +
            "&longitude=" +
            this.addressGetWhenClickMap.lng +
            "&description=" +
            this.queAns +
            "&country=" +
            this.addressGetWhenClickMap.properties.country,
          {
            method: "GET",
            headers: {
              "Content-type": "application/json;charset=UTF-8"
            }
          }
        )
          .then(response => response.json())
          .then(data => {
            this.queAns = null;
            this.errorShow = false;
            this.cursorAddHostPort = false
            this.hosportMarkers(true);
            this.removeInsertHostportLinkClickable()
            this.$gtag.event("inserted_hotspot", {
              event_category: "hotspots",
              event_label: "User Inserted New Hotspot Point",
              value: this.addressGetWhenClickMap.properties.name
            });
          });
      }
      else {
        this.errorShow = true;
      }
    },
    // remove marker route draw marker
    removeRouteDrawMarker() {
      if (this.RoutePlaceOneRemove !== null) {
        this.map.removeLayer(this.RoutePlaceOneRemove);
      }
      if (this.RoutePlaceSecondRemove !== null) {
        this.map.removeLayer(this.RoutePlaceSecondRemove);
      }
    },
    // remove marker selected place
    removePlaceMarkerSelectPlace () {
      if(this.setmarkerLalLng !== null) {
        this.map.removeLayer(this.setmarkerLalLng);
      }
      if(this.secendFeilds !== null) {
        this.map.removeLayer(this.secendFeilds);
      }
    },
    // remove Insert Hostport Link Clickable
    removeInsertHostportLinkClickable () {
      this.eventBus.$emit('removeInsertHostportLinkClickable')
    },

    closeModalQue () {
      setTimeout(() => {
        this.cursorAddHostPort = false
      }, 1000);
    },
    //   Insert Hostport Link Clickable
    hotspotMapLink () {
      this.$store.commit("setLeftSideBarIsOpen", false);
      setTimeout(() => {
        if(!this.$lowResolution) {
          if(!this.hosportStatus) {
            document.getElementById("hosportIdMobile").click(); 
          }
          setTimeout(() => {
            document.getElementsByClassName("desktop-insert")[0].click();
          }, 100);
          
        }
        else {
          this.hidesidebar();
          if(!this.mobilehosportStatus) {
            document.getElementById("MobileHostpot").click();
          }
          setTimeout(() => {
            document.getElementsByClassName("mobile-insert")[0].click();
          }, 100);
        }
        this.$router.push('/hotspotMap');
      }, 50);
    },
    setListeners() {
      const context = this;
      this.eventBus.$on('hotspotMapLink',() => {
        this.hotspotMapLink();
      })
      this.eventBus.$on('centerMapFouseTextfield',(data) => {
        this.centerMapFouseTextfield(data);
      })
      this.eventBus.$on("showLoadingRoute", value => {
        this.showLoading = value;
      });
      this.eventBus.$on('removePlaceMarkerSelectPlace',() => {
        this.routeSafetyTime = false;
        this.removePlaceMarkerSelectPlace();
      })
      this.eventBus.$on('cursorAddHostPort', (value) => {
        this.cursorAddHostPort = value
      })
      this.eventBus.$on('navigationMode', (value) => {
        this.navigationMode = value
      })
      this.eventBus.$on('addGira',() => {
        this.addGira();
      });
      this.eventBus.$on('removeGira', () => {
        this.removeGira();
      });
      this.eventBus.$on('addHostport',() => {
        this.addHostport();
      });
      this.eventBus.$on('removeHostport', () => {
        this.removeHostport();
      });
      this.eventBus.$on('hostportModalCloseOpenDropdwon',(value) => {
        this.hostportModalCloseOpenDropdwon = value
      })

      this.eventBus.$on('insertHostportshowMobileView',(value) => {
        this.insertHostportshowMobileView = value
      })
      // marker draw when route draw
      this.eventBus.$on("markerrouteone", () => {
        this.eventBus.$emit('routeCheck',true)
        this.eventBus.$emit('routeCheckMobile',true)
        if (this.RoutePlaceOneRemove !== null) {
          this.map.removeLayer(this.RoutePlaceOneRemove);
        }
        if (this.RoutePlaceSecondRemove !== null) {
          this.map.removeLayer(this.RoutePlaceSecondRemove);
        }
        let markers = GeoUtils.buildMarkersRoute([this.RoutePlaceOne], 0);
        this.RoutePlaceOneRemove = Leaflet.marker(
          [markers[0].position.lat, markers[0].position.lng],
          { icon: markers[0].icon }
        );
        let markerssecond;
        if(this.$store.getters.getMapstatus) {
          markerssecond = GeoUtils.buildMarkersPlaceName(
            [this.RoutePlaceSecond],
            'black'
            );
          } else {
            markerssecond = GeoUtils.buildMarkersPlaceName(
              [this.RoutePlaceSecond],
              'white'
              );
            }
            this.RoutePlaceSecondRemove = Leaflet.marker(
              [markerssecond[0].position.lat, markerssecond[0].position.lng],
              { icon: markerssecond[0].icon }
              );
              setTimeout(() => {
                if (this.$lowResolution) {
                  this.eventBus.$emit("giraStationRouteMobile");
                } else {
                  this.eventBus.$emit("giraStationRoute");
                }
                this.RoutePlaceOneRemove.addTo(this.map);
                this.RoutePlaceSecondRemove.addTo(this.map);
                this.polilineDraw = true
              }, 600);
      });
      //  route draw without gira sation
      this.eventBus.$on("simpleRouteDraw", () => { 
        if(this.$store.getters.dangeruousPoints !== '5') {
          this.routeSafetyTime = false;
          this.polilineDraw = false
          if (this.RoutePlaceOneRemove !== null) {
            this.map.removeLayer(this.RoutePlaceOneRemove);
          }
          if (this.RoutePlaceSecondRemove !== null) {
            this.map.removeLayer(this.RoutePlaceSecondRemove);
          }
          if (this.RoutePlaceOne !== null && this.RoutePlaceSecond !== null) {
            this.eventBus.$emit("GoogleAPI", this.RoutePlaceOne, 0,'gira');
            setTimeout(() => {
              this.eventBus.$emit("GoogleAPI", this.RoutePlaceSecond, 1,'gira');
              setTimeout(() => {
                this.eventBus.$emit("toggleRoundTripmobile");
              }, 500);
            }, 1000);
            if (this.$lowResolution) {
              this.eventBus.$emit("giraStationRouteMobile");
            } else {
              this.eventBus.$emit("giraStationRoute");
            }
          }
        }
      });
      //  route draw with gira sation
      this.eventBus.$on("giraRouteDraw", () => {
        if(this.$store.getters.dangeruousPoints !== '5') {

          this.routeSafetyTime = false;
          this.polilineDraw = false
          if (this.RoutePlaceOneRemove !== null) {
            this.map.removeLayer(this.RoutePlaceOneRemove);
          }
          if (this.RoutePlaceSecondRemove !== null) {
            this.map.removeLayer(this.RoutePlaceSecondRemove);
          }
          if (
            this.giraRoutePlaceOne !== null &&
            this.giraRoutePlaceSecond !== null
          ) {
            this.eventBus.$emit("GoogleAPI", this.giraRoutePlaceOne, 0,'gira');
            setTimeout(() => {
              this.eventBus.$emit("GoogleAPI", this.giraRoutePlaceSecond, 1,'gira');
              setTimeout(() => {
                this.eventBus.$emit("toggleRoundTripmobile");
              }, 500);
            }, 1000);
          }
        }
      });
      this.eventBus.$on("RoutePlaceOne", data => {
        this.RoutePlaceOne = data;
      });
      this.eventBus.$on("RoutePlaceSecond", data => {
        this.RoutePlaceSecond = data;
      });
      this.eventBus.$on("hosportStatus", value => {
        this.hosportStatus = value;
      });
      this.eventBus.$on("mobilehosportStatus", value => {
        this.mobilehosportStatus = value;
      });
      this.eventBus.$on("addressGetWhenClickMap", data => {
        console.log(data)
        this.addressGetWhenClickMap = data;
      });
      this.eventBus.$on("checkGiraStationStatus", value => {
        this.checkGiraStationStatus = value;
      });
      this.eventBus.$on("girastationone", value => {
        this.giraStationOne(value);
      });
      this.eventBus.$on("girastationsecond", value => {
        this.giraStationSecond(value);
      });
      this.eventBus.$on("removemarkersecond", () => {
        this.removemarkersecond();
      });
      this.eventBus.$on("removemarkerfirst", () => {
        this.removemarkerfirst();
      });
      this.eventBus.$on("clearMap", context.clearMap);
      this.eventBus.$on("routeShowInMap", () => {
        this.routehideshow = true;
        this.showActiveRouteData = true;
      });
      this.eventBus.$on("routeHideInMap", () => {
        this.routehideshow = false;
        this.showActiveRouteData = false;
      });
      this.eventBus.$on("setsecondyourlication", () => {
        this.updateMyLocationFirstTime(true);
      });
      this.eventBus.$on("setmarkerFalse", context.setmarkerFalse);
      this.eventBus.$on("dengerous", data => {
        context.dengerousArea(data);
      });
      this.eventBus.$on("polygonsDenger", data => {
        context.setpolygons(data);
      });

      this.eventBus.$on("changeActiveRouteIndex", context.setActiveRouteIndex);
      this.eventBus.$on("setdirectioncenter", context.fitAllFeatures);
      this.eventBus.$on("setmarker", data => {
        context.setmarkers(data);
      });
      this.eventBus.$on("setmarkerfirst", data => {
        this.setmarkerfirst(data);
      });
      this.eventBus.$on('perceptionPollModal' , () => {
        this.perceptionPollModal = true
      })
      this.eventBus.$on(
        "altitudeChartHoverIndexChanged",
        context.highlightRoutePoint
      );

      this.eventBus.$on(
        "mouseLeftChartAltitudeChart",
        context.removeRoutePoint
      );

      this.eventBus.$on("showAltitudeModal", () => {
        context.isAltitudeModalOpen = true;
      });

      this.eventBus.$on("mapSettingsChanged", context.setProviders);

      this.eventBus.$on("placeFocusChanged", context.placeFocusChanged);

      this.eventBus.$on(
        "highlightPolylineSections",
        context.highlightPolylineSections
      );
      this.eventBus.$on("testp", data => {
        this.getDangerPointCount();
        context.setValuePolygones(data);
        // this.$emit('polygonvaluecheck',this.isDanger)
      });
      this.eventBus.$on("areashow", value => {
        this.areashow = value;
      });

      this.eventBus.$on("redrawAndFitMap", data => {
        if (data.guid && data.guid === context.guid) {
          context.adjustMap();
        }
      });
      this.eventBus.$on("currentlatlngobj", data => {
        this.currentlatlng = data;
      });
      this.eventBus.$on("placelatlngobj", data => {
        this.placelatlng = data;
      });
      this.eventBus.$on("removePalceone", index => {
        this.removePlace(index);
      });
    },

    /**
     * Toggle the accessible mode by
     * storing the flag under the mapSettings store
     * to do so we get the current setting object, update it,
     * convert it to a stringified representation and
     * save it to the browsers local storage
     * @uses localStorage
     */
    toggleAccessibleMode() {
      let mapSettings = this.$store.getters.mapSettings;
      mapSettings.accessibleModeActive = !mapSettings.accessibleModeActive;

      this.$store.dispatch("saveSettings", mapSettings).then(() => {
        console.log("Settings saved");
      });
    },
    deviceChecks() {
      const IOS =
        navigator.userAgent.match(/(iPod|iPhone|iPad)/) &&
        navigator.userAgent.match(/AppleWebKit/);

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        if (!IOS) {
          window.addEventListener("deviceorientation", this.handlers, true);
        }
        if (isMobile && IOS !== null) {
          this.orientationCheck = true;
        }
      }
    },
    handlers(e) {
      const check = e.webkitCompassHeading || Math.abs(e.alpha - 360);
      if (e.alpha !== 0) {
        this.orientationCheck = true;
      } else {
        this.orientationCheck = false;
      }
    }
  },

  /**
   * Set the initial state of the map when the component is mounted
   * and call calling loadMapData, setProviders, setDrawingTool, storeMapBoundsAndSetMapAsReady.
   * @emits onCreate
   * @listens map.addInitHook and add a handler
   */
  mounted() {
    this.zoomLevel = this.initialZoom;
    // in url find hotspotMap redering this 
    const checkdirection = url.includes("hotspotMap");
          if (checkdirection) {
            if (this.$lowResolution) {
              setTimeout(() => {
                // document.getElementsByClassName("mobile-insert")[0].click();
                this.eventBus.$emit('tooptipShow')
                this.eventBus.$emit('cursorAddHostPort',true)
              }, 500);
            } else {
              setTimeout(() => {
                // document.getElementsByClassName("desktop-insert")[0].click();
                this.eventBus.$emit('tooptipShow')
                this.eventBus.$emit('cursorAddHostPort',true)
                this.eventBus.$emit('addInsertHostportLinkClickable')
              }, 500);
            }
          }
    // Define a unique identifier to the map component instance
    this.guid = Utils.guid();

    // When the box is created, it emit
    // an event to its parent telling the parent its guid
    this.$emit("onCreate", this.guid);

    this.setListeners();
    let el = document.getElementById('map-view');
    el.addEventListener('touchstart', function(e) {
      if(e.touches.length >= 2) {
        this.touchstatus = false;
        clearTimeout(this.TimeremoveClickEvent)
      }
    })
    // Add the gesture handling so that when the user is
    // scrolling a page (embed state) with an ors map it
    // will actually scroll the page and not the map
    Leaflet.Map.addInitHook("addHandler", "gestureHandling", GestureHandling);

    // Once the map component is mounted, load the map data
    this.loadMapData();
    this.setProviders();
    this.setDrawingTool();
    this.storeMapBoundsAndSetMapAsReady();
    // this.addMoreHostMarker();
    const sunset = getSunset(this.$store.getters.mapSettings.mapCenter.lat, this.$store.getters.mapSettings.mapCenter.lng)
    const sunrise = getSunrise(this.$store.getters.mapSettings.mapCenter.lat,this.$store.getters.mapSettings.mapCenter.lng, new Date())
    const sunriseHour = sunrise.getHours()
    const sunsetHour = sunset.getHours()
    const hours = new Date().getHours()
    const isDayTime = hours > sunriseHour && hours < sunsetHour
    this.$store.commit('setMapStatus',isDayTime)

  },
  /**
   * Set the local showClickPopups value
   * and set the map center on create
   */
  created() {
    // Copy the prop value to a local prop
    // so it can be modified locally
    this.imageGetAPI()
    this.showClickPopups = this.showPopups;
    this.localAvoidPolygons = this.avoidPolygons;
    this.loadAvoidPolygons();
    this.setMapCenter();
    this.deviceChecks();
    this.hosportMarkers();
    this.giraMarkers();
    // this.updateMyLocationFirstTime(true)
    
    window.addEventListener("keyup", this.disablePickPlaceMode);
    if (!localStorage.getItem("history_search")) {
      // console.log('hi')
      localStorage.setItem("history_search", JSON.stringify(this.a));
      // this.$cookie.set('history_search', JSON.stringify(this.a));
    }
  }
};
